// ─── ClientAppPreviewModal ──────────────────────────────────────────────
// Modale d'aperçu du JSON ClientPlan tel qu'il sera envoyé à l'app
// cliente premium (anissa-client-preview).
//
// V1 — étape "à blanc" : lecture seule du JSON construit via le mapper.
// Permet de valider visuellement le mapping avant migration DB + bouton
// "Publier dans l'app". Aucun appel réseau, aucune écriture.
//
// Props :
//   - client       : ligne `clients` du SaaS
//   - consultation : ligne `nutrition_consultations` (état COURANT, pas
//                    nécessairement persisté — c'est readEdited() qui le
//                    nourrit côté caller)
//   - onClose      : fermer la modale
//
// La modale recompute le JSON à chaque ouverture (cheap, le mapper
// est pur). Pas de cache, pas de sync.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildClientAppPlanFromConsultation,
  diagnoseClientAppPlan,
} from './services/clientAppMapper';
import {
  publishConsultationToClientApp,
  checkPublishConfig,
  PublishConfigError,
  PublishHttpError,
  PublishClinicalError,
  createPhaseTransitionPublicationConfirmation,
  resolveFollowupPublicationWeek,
} from './services/publishToClientApp';
import { formatClearanceForConfirm } from './services/clinicalClearance';
import {
  enrichClientAppPlan,
  applyEnrichmentToPlan,
  buildIntroLetterFromEnrichment,
  EnrichConfigError,
  EnrichHttpError,
} from './services/aiEnrichClientPlan';
import ErrorBoundary from './components/ErrorBoundary';
import Icon from './components/Icon';
import RepublicationNoticeBlock from './components/RepublicationNoticeBlock';
import MealPrepEditorialCard from './components/MealPrepEditorialCard';
import ModularMealPrepEditorialCard, { canUseModularMealPrep } from './components/ModularMealPrepEditorialCard';
import FourWeekRotationEditorialCard from './components/FourWeekRotationEditorialCard';
import ClientAppWorkspace from './components/ClientAppWorkspace';
import { useModalA11y } from './hooks/useModalA11y';
import { clearStatusCache, fetchClientsStatus } from './services/fetchClientsStatus';
import { getMealPrepPublicationIssue } from './services/mealPrep';
import { getFourWeekRotationPublicationIssue } from './services/fourWeekRotation';
import {
  buildFridgeSyncSourceRevision,
  buildFridgeSyncPreview,
  getFridgeSyncPublicationIssue,
} from './services/fridgeSync';
import { getPublicationConsultation } from './services/phaseTransitionService';
import { mgdReviewV2Client } from './services/mgdReviewV2Client';
import { externalPlanAuthorityClient } from './services/externalPlanAuthority';
import { isExternalPlanInput } from './services/externalPlanWorkflow';
import { sha256Hex } from './services/journeyState';
import { saveEditorialConsultationWithFreshExternalRequest } from './services/externalEditorialSave';
import {
  createSupplementEditorialOverride,
  parseSupplementEditorialOverride,
} from './services/supplementEditorialOverrides';
import { computeProgramCompleteness } from './services/programCompleteness';
import DeliveryFinalizationWorkspace from './components/DeliveryFinalizationWorkspace';
import {
  buildDeliveryWorkspaceModel,
  deliveryWorkspaceTarget,
} from './services/deliveryWorkspace';
import {
  getClientAppWorkspacePublicationIssue,
  isWeekEditorAuthorityStale,
  menusMatchAppliedContent,
  weekEditorSaveReclaimsSource,
  markClientAppWorkspaceWeekEditorSave,
  refreshClientAppWorkspaceWeekEditorAuthority,
} from './services/clientAppWorkspace';
import {
  buildWeekMenusModel,
  confirmNarrativeProseReview,
  narrativeProseReviewState,
  recordRotationValidation,
} from './services/weekMenus';
import WeekMenusEditor from './components/WeekMenusEditor';

const TABS = [
  { id: 'json',       label: 'JSON complet' },
  { id: 'sections',   label: 'Aperçu sections' },
  { id: 'diagnostic', label: 'Diagnostic' },
];

export function getEditorialPublicationIssue({
  savingEdits = false,
  hasUnsavedSectionEdits = false,
  mealPrepDirty = false,
  enriching = false,
  enrichmentDraftPending = false,
  supplementPublicationIssue = null,
  fourWeekRotationPublicationIssue = null,
  mealPrepPublicationIssue = null,
  fridgeSyncPublicationIssue = null,
  clientAppWorkspacePublicationIssue = null,
} = {}) {
  if (savingEdits) return 'Enregistrement des modifications en cours';
  if (enriching) {
    return 'Génération éditoriale en cours';
  }
  if (enrichmentDraftPending) return 'Proposition à accepter ou refuser';
  if (hasUnsavedSectionEdits) return 'Modifications éditoriales non enregistrées';
  if (mealPrepDirty) return 'Meal prep modifié mais pas encore enregistré';
  return clientAppWorkspacePublicationIssue || supplementPublicationIssue || fourWeekRotationPublicationIssue
    || mealPrepPublicationIssue || fridgeSyncPublicationIssue || null;
}

export default function ClientAppPreviewModal({
  client,
  consultation,
  aiActionsAllowed = true,
  publishAllowed = false,
  publicationCompletionRequired = false,
  initialSection = null,
  onClose,
  onOpenMessages,
  onPublished,
  onDeliveryProofStale,
}) {
  const [tab, setTab] = useState('sections');
  // « package » reste l'entrée par défaut, celle de « Préparer le dossier ».
  // `initialSection` sert quand Anissa entre par une pièce précise signalée
  // incomplète à l'étape Livraison : l'aperçu s'ouvre alors sur cette
  // section, au lieu de la lui faire chercher.
  const [activeWorkspaceSection, setActiveWorkspaceSection] = useState(
    () => deliveryWorkspaceTarget(initialSection || 'package'),
  );
  const [copied, setCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null); // { ok, plan_id, login_url, app_url, ... }
  // Une option non appliquée après publication : signalée, jamais bloquante.
  const [optionsWarning, setOptionsWarning] = useState(null);
  const [publishError, setPublishError] = useState(null);
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  // La publication et le passage Livraison -> Suivi sont deux écritures
  // distinctes. Si la seconde échoue, Anissa doit pouvoir la rejouer sans
  // créer une nouvelle version du programme dans l'app cliente.
  const [completionPending, setCompletionPending] = useState(false);
  const [completingPublication, setCompletingPublication] = useState(false);

  // ─── État enrichissement IA ─────────────────────────────────────────
  const [enriching, setEnriching] = useState(false);
  const [enrichmentDraft, setEnrichmentDraft] = useState(null); // proposition IA pas encore acceptée
  const [enrichmentApplied, setEnrichmentApplied] = useState(null); // accepté → injecté dans le plan publié
  const [enrichmentError, setEnrichmentError] = useState(null);
  // V97.13.45 — section ciblee par le bouton "Ameliorer" : 'intro' | 'strategy'
  // | 'fridge' | 'protocols' | null (global). Permet de filtrer le draft a la
  // seule section concernee plutot que d ecraser tout le plan.
  const [enrichmentTargetSection, setEnrichmentTargetSection] = useState(null);

  // V97.13.38 — État local de la consultation pour refleter les edits inline
  // (greeting, signature, ...) sans attendre un round-trip parent. Initialise
  // depuis la prop, mis a jour par les handlers de save.
  const [localConsultation, setLocalConsultation] = useState(consultation);
  const [savingEdits, setSavingEdits] = useState(false);
  const [editsSavedAt, setEditsSavedAt] = useState(null);
  const [editsError, setEditsError] = useState(null);
  const [mealPrepDirty, setMealPrepDirty] = useState(false);
  const [sectionDirtyMap, setSectionDirtyMap] = useState({});
  // Déclaré tôt : sert aussi à afficher un contenu déjà publié de façon neutre.
  const [statusEntry, setStatusEntry] = useState(null);
  const panelRef = useRef(null);
  const workspaceSaveAndCloseRef = useRef(null);
  const handleWorkspaceClose = useCallback(() => {
    if (workspaceSaveAndCloseRef.current) {
      return workspaceSaveAndCloseRef.current();
    }
    onClose?.();
    return true;
  }, [onClose]);
  const modalBusy = publishing || enriching || completingPublication
    || savingEdits;
  useModalA11y({ onClose: handleWorkspaceClose, busy: modalBusy, panelRef });
  const [phaseTransitionEditingLocked, setPhaseTransitionEditingLocked] = useState(false);
  const phaseTransitionEditingLockedRef = useRef(false);
  const handleSectionDirtyChange = useCallback((section, dirty) => {
    setSectionDirtyMap((current) => {
      if (current[section] === dirty) return current;
      return { ...current, [section]: dirty };
    });
  }, []);
  const hasUnsavedSectionEdits = Object.values(sectionDirtyMap).some(Boolean);
  // Re-sync si la prop consultation change (ex: parent reload apres publish)
  useEffect(() => {
    setLocalConsultation(consultation);
    setSectionDirtyMap({});
    setMealPrepDirty(false);
  }, [consultation]);

  // La propriété éphémère appartient à la prop canonique du parent. Ne jamais
  // la reprendre depuis une copie locale qui pourrait dater d'avant la RPC de
  // finalisation avec le même id de consultation.
  const phaseTransitionRun = consultation?.phaseTransitionRun || null;
  const editorialLocked = Boolean(phaseTransitionRun && phaseTransitionEditingLocked);
  const publicationConsultationState = useMemo(() => {
    try {
      const value = phaseTransitionRun
        ? getPublicationConsultation(localConsultation, phaseTransitionRun)
        : localConsultation;
      return { value, error: null };
    } catch (phaseError) {
      return { value: null, error: phaseError?.message || 'Transition de phase invalide.' };
    }
  }, [localConsultation, phaseTransitionRun]);
  const publicationConsultation = publicationConsultationState.value;
  const assertFreshAiAllowed = useCallback(async () => {
    const status = await mgdReviewV2Client.status(String(client?.id || ''));
    const externalInput = publicationConsultation?.editorial_overrides?.external_plan_input
      ?? publicationConsultation?.editorialOverrides?.external_plan_input;
    if (isExternalPlanInput(externalInput)) {
      const text = publicationConsultation?.nutrition_plan
        ?? publicationConsultation?.nutritionPlan ?? '';
      const planHash = await sha256Hex(String(text).replace(/\r\n/g, '\n'));
      const authority = await externalPlanAuthorityClient.status({
        clientId: String(client?.id || ''),
        consultationId: String(publicationConsultation?.id || ''),
        planHash,
        profileSummaryHash: externalInput.profile_summary_sha256,
      });
      if (!authority.authorized || !authority.persisted || !authority.validated) {
        throw new Error('Le plan externe courant doit être validé avant cette action.');
      }
    } else if (status.reports.length > 0) {
      if (publicationConsultation?.id) {
        await mgdReviewV2Client.planProofStatus({ clientId: String(client.id),
          consultationId: String(publicationConsultation.id) });
      }
      throw new Error('Cette action est indisponible pour cette ancienne version du dossier.');
    }
    if (!aiActionsAllowed) {
      throw new Error('Préparation indisponible tant que l’autorité du dossier n’est pas confirmée.');
    }
    return true;
  }, [aiActionsAllowed, client?.id, publicationConsultation]);
  const effectivePublicationCompletionRequired = publicationCompletionRequired
    || Boolean(phaseTransitionRun);

  // Construire le plan brut. useMemo évite recompute à chaque changement de
  // tab — la dépendance est l'identité des inputs.
  const { rawPlan, error } = useMemo(() => {
    if (publicationConsultationState.error) {
      return { rawPlan: null, error: publicationConsultationState.error };
    }
    try {
      const p = buildClientAppPlanFromConsultation(client, publicationConsultation);
      return { rawPlan: p, error: null };
    } catch (err) {
      // V94.67 : log le stack complet en console pour diagnostic.
      // Le message UI seul ("e is undefined at split") ne dit pas QUEL .split
      // a planté ni avec quel input. Le stack pointe vers la ligne exacte du
      // mapper, indispensable pour corriger sans patcher a l'aveugle.
      // eslint-disable-next-line no-console
      console.error('[ClientAppPreviewModal] mapping error', err, {
        clientId: client?.id,
        consultationId: publicationConsultation?.id,
      });
      return { rawPlan: null, error: err?.message || String(err) };
    }
  }, [client, publicationConsultation, publicationConsultationState.error]);

  // Plan affiché = brut + enrichissement appliqué si présent.
  // V97.112 : try/catch — un enrichissement malformé ne doit JAMAIS crasher le
  // rendu (avant : écran noir). On dégrade sur le plan brut.
  const plan = useMemo(() => {
    if (!rawPlan || !enrichmentApplied) return rawPlan;
    try {
      return applyEnrichmentToPlan(rawPlan, enrichmentApplied);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ClientAppPreviewModal] enrichment apply failed', err);
      return rawPlan;
    }
  }, [rawPlan, enrichmentApplied]);
  // Vue source des compléments, sans le filtre app-only. Elle reste locale à
  // la modale et permet à Anissa de restaurer une carte masquée. Le DTO publié
  // continue d'utiliser `plan`, où le mapper applique les exclusions.
  const supplementSourceProtocols = useMemo(() => {
    if (!publicationConsultation || publicationConsultationState.error) return null;
    try {
      const overrides = publicationConsultation.editorial_overrides || {};
      const withoutSupplementExclusions = { ...overrides };
      delete withoutSupplementExclusions.supplements;
      const sourcePlan = buildClientAppPlanFromConsultation(client, {
        ...publicationConsultation,
        editorial_overrides: withoutSupplementExclusions,
      });
      return sourcePlan?.sections?.protocols_data || null;
    } catch {
      return null;
    }
  }, [client, publicationConsultation, publicationConsultationState.error]);
  const diag = useMemo(() => (plan ? diagnoseClientAppPlan(plan) : null), [plan]);
  const completeness = useMemo(
    () => computeProgramCompleteness(client, publicationConsultation),
    [client, publicationConsultation],
  );

  const cfgCheck = useMemo(() => checkPublishConfig(), []);
  const clientEmail = client?.form?.email || client?.email || null;
  const publishesMealPrepV2 = rawPlan?.sections?.week_meals?.prep?.version === 2;
  const mealPrepPublicationIssue = useMemo(() => publishesMealPrepV2 ? null : getMealPrepPublicationIssue(
    rawPlan?.sections?.week_meals,
    localConsultation?.editorial_overrides?.meal_prep,
  ), [publishesMealPrepV2, rawPlan?.sections?.week_meals, localConsultation?.editorial_overrides?.meal_prep]);
  const fourWeekRotationPublicationIssue = useMemo(() => getFourWeekRotationPublicationIssue(
    rawPlan?.sections?.week_meals,
    localConsultation?.editorial_overrides?.four_week_rotation,
  ), [
    rawPlan?.sections?.week_meals,
    localConsultation?.editorial_overrides?.four_week_rotation,
  ]);
  const supplementPublicationIssue = useMemo(() => {
    const value = localConsultation?.editorial_overrides?.supplements;
    return value !== null && value !== undefined && !parseSupplementEditorialOverride(value)
      ? 'Compléments de l’app à vérifier avec Anissa'
      : null;
  }, [localConsultation?.editorial_overrides?.supplements]);
  const fridgeSyncPublicationIssue = useMemo(() => publishesMealPrepV2 ? null : getFridgeSyncPublicationIssue(
    rawPlan?.sections?.week_meals,
    localConsultation?.editorial_overrides?.meal_prep,
    rawPlan?.sections?.fridge_data,
    localConsultation?.editorial_overrides?.fridge_sync,
  ), [
    publishesMealPrepV2,
    rawPlan?.sections?.week_meals,
    rawPlan?.sections?.fridge_data,
    localConsultation?.editorial_overrides?.meal_prep,
    localConsultation?.editorial_overrides?.fridge_sync,
  ]);
  const clientAppWorkspacePublicationIssue = useMemo(() => getClientAppWorkspacePublicationIssue({
    client,
    consultation: localConsultation,
    directives: localConsultation?.aiDirectives || localConsultation?.ai_directives || '',
  }), [client, localConsultation]);
  const editorialPublicationIssue = getEditorialPublicationIssue({
    savingEdits,
    hasUnsavedSectionEdits,
    mealPrepDirty,
    enriching,
    enrichmentDraftPending: Boolean(enrichmentDraft),
    supplementPublicationIssue,
    fourWeekRotationPublicationIssue,
    mealPrepPublicationIssue,
    fridgeSyncPublicationIssue,
    clientAppWorkspacePublicationIssue,
  });
  // Même modèle pour l'éditeur Semaine, la carte Rotation et l'étape
  // Livraison : un seul réservoir par repas, une seule rotation.
  // Programme déjà publié depuis cette consultation : un contenu jamais relu
  // dans l'éditeur s'affiche de façon neutre, pas comme une alerte.
  // Et seulement si les menus actuels sont exactement ceux appliqués : sinon,
  // ce qui est affiché n'est pas ce qui a été publié (« À valider »).
  const menusAlreadyPublished = statusEntry?.plan_status === 'published'
    && String(statusEntry?.source_consultation_id || '') === String(localConsultation?.id || '')
    && menusMatchAppliedContent(localConsultation);
  const weekEditorReclaimsSource = weekEditorSaveReclaimsSource(localConsultation);
  // Relecture des textes rédigés au regard des exclusions écrites en prose.
  // Mêmes champs que le livrable : titre, objectif, sections, parcours.
  const narrativeReviewPayload = useMemo(() => (rawPlan ? {
    title: rawPlan.title ?? null,
    objective: rawPlan.objective ?? null,
    sections: rawPlan.sections ?? null,
    journey_phases: rawPlan.journey_phases || null,
  } : null), [rawPlan]);
  // Cette relecture n'est exigée que pour un programme MGD (hors plan
  // externe), seul cas où la publication la vérifie : ailleurs, la case
  // n'apparaît pas. Statut relu à l'ouverture ; en cas d'échec, rien n'est
  // affiché et la publication reste contrôlée côté service.
  const [mgdActive, setMgdActive] = useState(false);
  const mgdClientId = String(client?.id || '');
  const mgdExternalMode = isExternalPlanInput(
    publicationConsultation?.editorial_overrides?.external_plan_input
      ?? publicationConsultation?.editorialOverrides?.external_plan_input,
  );
  useEffect(() => {
    let alive = true;
    if (!mgdClientId || mgdExternalMode) {
      setMgdActive(false);
      return () => { alive = false; };
    }
    mgdReviewV2Client.status(mgdClientId)
      .then((status) => { if (alive) setMgdActive(Array.isArray(status?.reports) && status.reports.length > 0); })
      .catch(() => { if (alive) setMgdActive(false); });
    return () => { alive = false; };
  }, [mgdClientId, mgdExternalMode]);
  const narrativeProseReview = useMemo(() => (narrativeReviewPayload && mgdActive
    ? narrativeProseReviewState({ client, consultation: localConsultation, payload: narrativeReviewPayload })
    : null), [client, localConsultation, mgdActive, narrativeReviewPayload]);
  const weekEditorAuthorityStale = useMemo(
    () => isWeekEditorAuthorityStale(localConsultation),
    [localConsultation],
  );
  const weekMenusModel = useMemo(() => {
    if (!publicationConsultation || !rawPlan) return null;
    try {
      return buildWeekMenusModel({
        client,
        consultation: publicationConsultation,
        weekMeals: rawPlan.sections?.week_meals || null,
        alreadyPublished: menusAlreadyPublished,
      });
    } catch {
      return null;
    }
  }, [client, menusAlreadyPublished, publicationConsultation, rawPlan]);
  const deliveryWorkspaceModel = useMemo(() => buildDeliveryWorkspaceModel({
    plan,
    completeness,
    editorialIssue: editorialPublicationIssue,
    weekMenus: weekMenusModel,
  }), [completeness, editorialPublicationIssue, plan, weekMenusModel]);
  const handleWorkspaceNavigate = useCallback((sectionId) => {
    const target = deliveryWorkspaceTarget(sectionId);
    setActiveWorkspaceSection(target);
    if (tab !== 'sections') setTab('sections');
    window.requestAnimationFrame(() => {
      const content = document.getElementById('delivery-workspace-content');
      content?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [tab]);
  const handleWorkspaceFullPreview = useCallback(() => {
    setActiveWorkspaceSection((current) => current === 'all' ? 'introduction' : 'all');
    if (tab !== 'sections') setTab('sections');
    window.requestAnimationFrame(() => {
      const content = document.getElementById('delivery-workspace-content');
      content?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [tab]);
  const saveEditorialConsultation = async (nextConsultation) => {
    if (phaseTransitionEditingLockedRef.current) {
      throw new Error('La publication de cette phase a commencé. Recharge la version canonique avant toute modification.');
    }
    const { saveNutritionConsultationConfirmed } = await import('./store');
    if (phaseTransitionEditingLockedRef.current) {
      throw new Error('La publication de cette phase a commencé. Aucune modification n’a été enregistrée.');
    }
    return saveEditorialConsultationWithFreshExternalRequest({
      consultation: nextConsultation,
      save: saveNutritionConsultationConfirmed,
    });
  };

  // V97.46 (Lot 3 A−) — "Conscience de ré-publication". Lecture seule des
  // métadonnées clients-status déjà disponibles (cache 60s partagé avec les
  // badges → souvent un cache hit). Sert uniquement à informer Anissa qu'un
  // programme est déjà publié. Aucune écriture, aucun nouvel endpoint.
  const [publicationRecoveryState, setPublicationRecoveryState] = useState('idle');
  const [statusRefreshNonce, setStatusRefreshNonce] = useState(0);
  useEffect(() => {
    let alive = true;
    const stagingClientId = client?.stagingClientId || client?.staging_client_id || null;
    setStatusEntry(null);
    if (!clientEmail && !stagingClientId) {
      setPublicationRecoveryState(effectivePublicationCompletionRequired ? 'error' : 'idle');
      return;
    }
    // Une ouverture de la fenetre Livraison doit interroger la verite distante,
    // pas un cache pris avant une publication dont la reponse aurait ete perdue.
    if (effectivePublicationCompletionRequired) {
      setPublicationRecoveryState('checking');
      clearStatusCache();
    } else {
      setPublicationRecoveryState('idle');
    }
    fetchClientsStatus(
      [{ email: clientEmail, stagingClientId }],
      { strict: effectivePublicationCompletionRequired },
    )
      .then((map) => {
        if (!alive) return;
        const entry =
          (stagingClientId && map[`id:${stagingClientId}`]) ||
          (clientEmail && map[clientEmail]) ||
          null;
        setStatusEntry(entry);
        if (effectivePublicationCompletionRequired) setPublicationRecoveryState('ready');
      })
      .catch(() => {
        if (!alive) return;
        setStatusEntry(null);
        if (effectivePublicationCompletionRequired) setPublicationRecoveryState('error');
      });
    return () => { alive = false; };
  }, [
    clientEmail,
    client?.stagingClientId,
    client?.staging_client_id,
    effectivePublicationCompletionRequired,
    statusRefreshNonce,
  ]);

  // Reprise durable derivee pendant le rendu : aucun intervalle ne peut
  // reactiver Publier entre la preuve distante et le bouton de finalisation.
  const recoveredPublishResult = useMemo(() => recoverPublishedJourneyResult({
    statusEntry,
    consultationId: publicationConsultation?.id,
    completionRequired: effectivePublicationCompletionRequired && publicationRecoveryState === 'ready',
  }), [
    publicationConsultation?.id,
    effectivePublicationCompletionRequired,
    publicationRecoveryState,
    statusEntry,
  ]);
  const effectivePublishResult = publishResult || recoveredPublishResult;
  const effectiveCompletionPending = completionPending || Boolean(recoveredPublishResult && !publishResult);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(plan, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  const handlePublish = async (options = {}) => {
    if (!canPublishFromPreview(publishAllowed)) {
      setPublishError('Publication disponible depuis l’étape Livraison.');
      setConfirmingPublish(false);
      return;
    }
    if (!canPublishAfterRecoveryCheck(effectivePublicationCompletionRequired, publicationRecoveryState)
        || recoveredPublishResult) {
      setPublishError('Vérification de la publication en cours ou à relancer.');
      setConfirmingPublish(false);
      return;
    }
    if (editorialPublicationIssue) {
      setPublishError(editorialPublicationIssue);
      setConfirmingPublish(false);
      return;
    }
    if (phaseTransitionRun) {
      // Verrou synchrone avant le premier await : aucune opération éditoriale
      // déjà lancée ne peut full-save la consultation stale après finalisation.
      phaseTransitionEditingLockedRef.current = true;
      setPhaseTransitionEditingLocked(true);
    }
    setPublishError(null);
    setPublishResult(null);
    setPublishing(true);
    try {
      // V97.13.38 : utilise localConsultation pour inclure les edits inline
      // (greeting, signature) qui auraient ete sauves mais pas encore propages
      // a la prop consultation par le parent.
      const gated = await executePreviewPublication(
        publishAllowed,
        async () => {
          const phaseTransitionConfirmation = phaseTransitionRun
            ? await createPhaseTransitionPublicationConfirmation({
                client,
                consultation: publicationConsultation,
                enrichment: enrichmentApplied,
                options,
                explicitlyConfirmed: true,
              })
            : null;
          const publish = (clinicalOverride) => publishConsultationToClientApp(
            client,
            publicationConsultation,
            enrichmentApplied,
            clinicalOverride
              ? { ...options, phaseTransitionConfirmation, clinicalOverride: true }
              : { ...options, phaseTransitionConfirmation },
          );
          try {
            return await publish(false);
          } catch (clinicalError) {
            // Plan externe seulement : le service dit si un override conscient
            // est possible. Anissa confirme explicitement, l'audit est écrit
            // avant toute sortie ; refus = publication bloquée comme avant.
            if (!(clinicalError instanceof PublishClinicalError)
                || clinicalError.overridable !== true
                || !window.confirm(formatClearanceForConfirm(clinicalError.verdict))) {
              throw clinicalError;
            }
            return publish(true);
          }
        },
      );
      const res = gated.result;
      setPublishResult(res);
      // L'espace vient d'être créé : on y pose le choix qu'Anissa avait réglé
      // avant de publier. Volontairement non bloquant — la publication a
      // abouti, une option non appliquée se recoche d'un clic et ne doit
      // jamais faire croire à un échec.
      if (!res?.staged) {
        try {
          const [{ applyPendingClientAppOptions }, { updateClientAppConfig }] = await Promise.all([
            import('./services/clientAppOptions'),
            import('./services/clientAppConfig'),
          ]);
          const pose = await applyPendingClientAppOptions(client, { update: updateClientAppConfig });
          if (pose.error) setOptionsWarning(pose.error);
        } catch (optionsError) {
          setOptionsWarning(optionsError?.message || 'Les options n’ont pas pu être appliquées.');
        }
      }
      // V97.99 : publication centralisée ici (chemin unique). On notifie le
      // parent pour qu'il avance le parcours si nécessaire (markDelivered).
      try {
        const completedResult = await completePublishedJourney(onPublished, res);
        if (completedResult?.ok) {
          setPublishResult({
            ...res,
            ...completedResult,
            saas_publication_stamp_warning: res.saas_publication_stamp_warning || null,
          });
        }
        setCompletionPending(false);
      } catch (completionError) {
        setCompletionPending(true);
        throw completionError;
      }
    } catch (err) {
      if (err instanceof PublishClinicalError) {
        setPublishError('Publication bloquée : corrige les points cliniques signalés puis valide à nouveau le plan.');
      } else if (err instanceof PublishConfigError) {
        if (err.message === 'Preuve du livrable application invalide.'
            && typeof onDeliveryProofStale === 'function') {
          onDeliveryProofStale();
          // Sans cette phrase, la publication était refusée en silence : la
          // fenêtre se fermait, la page repassait en « à revalider », et
          // Anissa croyait avoir publié. On dit ce qui s'est passé et quoi
          // faire, sinon on tourne en rond.
          setPublishError(
            'Publication arrêtée : le contenu de l’application a changé depuis sa dernière validation. '
            + 'Rien n’a été envoyé à la cliente. Ferme cette fenêtre, clique sur « Revalider le contenu de l’app », puis republie.',
          );
        } else {
          setPublishError(`Config : ${err.message}`);
        }
      } else if (err instanceof PublishHttpError) {
        setPublishError(`Erreur publication (${err.status}) : ${err.message}`);
      } else {
        setPublishError(err?.message || String(err));
      }
    } finally {
      // Le prochain chargement doit voir la publication distante qui vient
      // d'etre tentee, y compris si la reponse HTTP s'est perdue.
      clearStatusCache();
      if (effectivePublicationCompletionRequired) {
        // Ferme le bouton dans le meme batch que la fin de tentative. Le useEffect
        // ne fait ensuite que lancer la lecture distante, jamais fermer en retard.
        setPublicationRecoveryState('checking');
        setStatusEntry(null);
        setStatusRefreshNonce((value) => value + 1);
      }
      setPublishing(false);
      setConfirmingPublish(false);
    }
  };

  const handleRetryCompletion = async () => {
    if (!effectivePublishResult?.ok || typeof onPublished !== 'function') return;
    setPublishError(null);
    setCompletingPublication(true);
    try {
      const completedResult = await completePublishedJourney(onPublished, effectivePublishResult);
      if (completedResult?.ok) setPublishResult(completedResult);
      setCompletionPending(false);
    } catch (err) {
      setCompletionPending(true);
      setPublishError(err?.message || (phaseTransitionRun
        ? 'Le livrable est préparé, mais l’activation de la phase reste à finaliser.'
        : 'Le programme est publié, mais le passage au Suivi reste à finaliser.'));
    } finally {
      setCompletingPublication(false);
    }
  };

  const handleEnrich = async () => {
    if (!rawPlan || phaseTransitionEditingLockedRef.current) return;
    setEnrichmentError(null);
    setEnrichmentDraft(null);
    setEnriching(true);
    try {
      await assertFreshAiAllowed();
      // On appelle l'IA sur le plan BRUT (sans enrichissement préexistant)
      // pour qu'elle reparte de la matière source à chaque génération.
      const enrichment = await enrichClientAppPlan(rawPlan, client);
      if (phaseTransitionEditingLockedRef.current) return;
      setEnrichmentDraft(enrichment);
    } catch (err) {
      if (err instanceof EnrichConfigError) {
        setEnrichmentError(`Config : ${err.message}`);
      } else if (err instanceof EnrichHttpError) {
        setEnrichmentError(`Erreur de préparation (${err.status}) : ${err.message}`);
      } else {
        setEnrichmentError(err?.message || String(err));
      }
    } finally {
      setEnriching(false);
    }
  };

  // V97.13.45 — Enrichissement cible par section. Le user clique "Ameliorer"
  // sur une section specifique → on lance le meme enrichissement IA global
  // mais on FILTRE l'application aux seuls champs concernes par cette section.
  // Plus de "click frigo regenere tout le plan" — c'etait deroutant.
  const handleEnrichForSection = async (section) => {
    setEnrichmentTargetSection(section);
    await handleEnrich();
  };

  // Filtre un enrichissement complet aux seuls champs d'une section donnee.
  // Permet d'appliquer un enrichissement sans ecraser les autres sections.
  const filterEnrichmentToSection = (enrichment, section) => {
    if (!enrichment || !section) return enrichment;
    switch (section) {
      case 'intro':
        return {
          intro_body: enrichment.intro_body,
          pull_quote: enrichment.pull_quote,
          tailored_points: enrichment.tailored_points,
          signature_phrase: enrichment.signature_phrase,
        };
      case 'strategy':
        return {
          signature_phrase: enrichment.signature_phrase,
        };
      case 'fridge':
        return {
          section_intros: { fridge: enrichment.section_intros?.fridge },
        };
      case 'protocols':
        return {
          section_intros: { protocols: enrichment.section_intros?.protocols },
        };
      default:
        return enrichment;
    }
  };

  const handleAcceptEnrichment = async () => {
    if (phaseTransitionEditingLockedRef.current) return;
    const toApply = enrichmentTargetSection
      ? filterEnrichmentToSection(enrichmentDraft, enrichmentTargetSection)
      : enrichmentDraft;
    setEnrichmentError(null);
    try {
      // L'introduction acceptée doit survivre à la fermeture/recharge et être
      // la même source pour l'aperçu, la preuve et la publication. Auparavant,
      // elle restait seulement en mémoire dans enrichmentApplied.
      const nextIntro = buildIntroLetterFromEnrichment(
        localConsultation?.intro_letter,
        toApply,
      );
      if (nextIntro) {
        const updated = await saveEditorialConsultation({
          ...localConsultation,
          intro_letter: nextIntro,
        });
        setLocalConsultation(updated);
        setEditsSavedAt(Date.now());
      }
      setEnrichmentApplied(toApply);
      setEnrichmentDraft(null);
      setEnrichmentTargetSection(null);
    } catch (error) {
      setEnrichmentError(error?.message || 'Le contenu accepté n’a pas pu être enregistré.');
    }
  };

  const handleRejectEnrichment = () => {
    if (phaseTransitionEditingLockedRef.current) return;
    setEnrichmentDraft(null);
    setEnrichmentTargetSection(null);
  };

  const handleResetEnrichment = () => {
    if (phaseTransitionEditingLockedRef.current) return;
    setEnrichmentApplied(null);
    setEnrichmentDraft(null);
  };

  // Semaine, menus & recettes — l'éditeur prépare la consultation suivante via
  // le service weekMenus (recettes déplacées avec les titres, descriptions
  // conservées). Ici : uniquement le passage par l'enregistrement confirmé.
  // Un enregistrement de contenu rend ces menus prioritaires dans le Dossier
  // application et remet ses sections concernées en relecture ; une simple
  // validation de repas ne touche pas au dossier.
  // Confirmation explicite, liée aux textes exacts et aux exclusions ; ne
  // valide rien d'autre.
  const handleConfirmNarrativeProseReview = async () => {
    if (!localConsultation || !narrativeReviewPayload || phaseTransitionEditingLockedRef.current) return;
    const result = confirmNarrativeProseReview({
      client,
      consultation: localConsultation,
      payload: narrativeReviewPayload,
    });
    if (!result.ok) {
      setEditsError(result.error);
      return;
    }
    setSavingEdits(true);
    setEditsError(null);
    try {
      const updated = await saveEditorialConsultation(result.consultation);
      setLocalConsultation(updated);
    } catch (err) {
      setEditsError(err?.message || String(err));
    } finally {
      setSavingEdits(false);
    }
  };

  const handleSaveWeekMenus = async (nextConsultation, { kind = 'content' } = {}) => {
    if (!nextConsultation || !localConsultation || phaseTransitionEditingLockedRef.current) {
      throw new Error('Consultation indisponible ou verrouillée pour publication.');
    }
    setEditsError(null);
    setSavingEdits(true);
    try {
      const toSave = kind === 'content'
        ? markClientAppWorkspaceWeekEditorSave(nextConsultation)
        : nextConsultation;
      const updated = await saveEditorialConsultation(toSave);
      setLocalConsultation(updated);
      return updated;
    } finally {
      setSavingEdits(false);
    }
  };

  const handleSaveMealPrep = async (mealPrep) => {
    if (!localConsultation || phaseTransitionEditingLockedRef.current) {
      throw new Error('Consultation indisponible ou verrouillée pour publication.');
    }
    const existingOverrides = localConsultation.editorial_overrides || {};
    const updated = await saveEditorialConsultation({
      ...localConsultation,
      editorial_overrides: {
        ...existingOverrides,
        meal_prep: mealPrep,
      },
    });
    setLocalConsultation(updated);
  };

  const handleSaveMealPrepV2 = async (mealPrepV2) => {
    if (!localConsultation || phaseTransitionEditingLockedRef.current) {
      throw new Error('Consultation indisponible ou verrouillée pour publication.');
    }
    const existingOverrides = localConsultation.editorial_overrides || {};
    const updated = await saveEditorialConsultation({
      ...localConsultation,
      editorial_overrides: {
        ...existingOverrides,
        meal_prep_v2: mealPrepV2,
      },
    });
    setLocalConsultation(updated);
  };

  const handleSaveFourWeekRotation = async (fourWeekRotation) => {
    if (!localConsultation || phaseTransitionEditingLockedRef.current) {
      throw new Error('Consultation indisponible ou verrouillée pour publication.');
    }
    const existingOverrides = localConsultation.editorial_overrides || {};
    let next = {
      ...localConsultation,
      editorial_overrides: {
        ...existingOverrides,
        four_week_rotation: fourWeekRotation,
      },
    };
    // La validation garde l'empreinte exacte de chaque repas relu : une
    // modification ultérieure n'invalidera que le repas concerné.
    if (fourWeekRotation?.status === 'validated') {
      next = recordRotationValidation({ client, consultation: next });
    }
    const updated = await saveEditorialConsultation(refreshClientAppWorkspaceWeekEditorAuthority(next));
    setLocalConsultation(updated);
  };

  const handleSaveFridgeSync = async (fridgeSync) => {
    if (!localConsultation || phaseTransitionEditingLockedRef.current) {
      throw new Error('Consultation indisponible ou verrouillée pour publication.');
    }
    const existingOverrides = localConsultation.editorial_overrides || {};
    const updated = await saveEditorialConsultation({
      ...localConsultation,
      editorial_overrides: {
        ...existingOverrides,
        fridge_sync: fridgeSync,
      },
    });
    setLocalConsultation(updated);
  };

  // V97.13.43 Phase A3c — Sauvegarde des edits Frigo.
  // Persiste dans consultation.editorial_overrides.fridge.
  // Structure : { to_favor: string[], to_limit: string[] }
  const handleSaveFridgeEdits = async (edits) => {
    if (!edits || !localConsultation || phaseTransitionEditingLockedRef.current) return;
    setEditsError(null);
    setSavingEdits(true);
    try {
      const existingOverrides = localConsultation.editorial_overrides || {};
      const nextOverrides = {
        ...existingOverrides,
        fridge: {
          ...(existingOverrides.fridge || {}),
          ...(Array.isArray(edits.to_favor) ? { to_favor: edits.to_favor.map((s) => String(s || '').trim()).filter(Boolean) } : {}),
          ...(Array.isArray(edits.to_limit) ? { to_limit: edits.to_limit.map((s) => String(s || '').trim()).filter(Boolean) } : {}),
        },
      };
      const updated = await saveEditorialConsultation({
        ...localConsultation,
        editorial_overrides: nextOverrides,
      });
      setLocalConsultation(updated);
      setEditsSavedAt(Date.now());
      setTimeout(() => setEditsSavedAt((v) => (v && Date.now() - v >= 2400 ? null : v)), 2500);
    } catch (err) {
      setEditsError(err?.message || String(err));
    } finally {
      setSavingEdits(false);
    }
  };

  // Sauvegarde app-only : intro + IDs des cartes masquées. Le champ source
  // `consultation.supplements` reste inchangé et continue d'alimenter le Word.
  const handleSaveSupplementsEdits = async (edits) => {
    if (!edits || typeof edits.intro !== 'string' || !localConsultation || phaseTransitionEditingLockedRef.current) return;
    setEditsError(null);
    setSavingEdits(true);
    try {
      const existingOverrides = localConsultation.editorial_overrides || {};
      const nextOverrides = {
        ...existingOverrides,
        supplements_intro: edits.intro.trim(),
        supplements: createSupplementEditorialOverride(edits.excluded_item_ids),
      };
      const updated = await saveEditorialConsultation({
        ...localConsultation,
        editorial_overrides: nextOverrides,
      });
      setLocalConsultation(updated);
      setEditsSavedAt(Date.now());
      setTimeout(() => setEditsSavedAt((v) => (v && Date.now() - v >= 2400 ? null : v)), 2500);
    } catch (err) {
      setEditsError(err?.message || String(err));
      throw err;
    } finally {
      setSavingEdits(false);
    }
  };

  // V97.13.41 Phase A3a — Sauvegarde des edits Strategie.
  // Persiste dans consultation.editorial_overrides.strategy (champ JSONB
  // generique). Necessite migration Supabase pour le sync cloud.
  const handleSaveStrategyEdits = async (edits) => {
    if (!edits || !localConsultation || phaseTransitionEditingLockedRef.current) return;
    setEditsError(null);
    setSavingEdits(true);
    try {
      const existingOverrides = localConsultation.editorial_overrides || {};
      const nextStrategy = {
        ...(existingOverrides.strategy || {}),
        pillars: Array.isArray(edits.pillars)
          ? edits.pillars
              .map((p) => ({
                title: String(p?.title || '').trim(),
                description: String(p?.description || '').trim(),
              }))
              .filter((p) => p.title && p.description)
          : (existingOverrides.strategy?.pillars || []),
      };
      const nextOverrides = {
        ...existingOverrides,
        strategy: nextStrategy,
      };
      const updated = await saveEditorialConsultation({
        ...localConsultation,
        editorial_overrides: nextOverrides,
      });
      setLocalConsultation(updated);
      setEditsSavedAt(Date.now());
      setTimeout(() => setEditsSavedAt((v) => (v && Date.now() - v >= 2400 ? null : v)), 2500);
    } catch (err) {
      setEditsError(err?.message || String(err));
    } finally {
      setSavingEdits(false);
    }
  };

  // V97.13.38 + A2 V97.13.39 — Sauvegarde des edits manuels inline.
  // Champs supportes : greeting, signature (A1), body[] + pull_quote (A2).
  // Merge dans consultation.intro_letter (deja persiste cote DB et lu en
  // priorite par le mapper).
  const handleSaveIntroEdits = async (edits) => {
    if (!edits || !localConsultation || phaseTransitionEditingLockedRef.current) return;
    setEditsError(null);
    setSavingEdits(true);
    try {
      const existing = localConsultation.intro_letter || {};
      // Si on a un draft enrichi non encore applique, ne pas le perdre :
      // on prend en priorite l'edit explicite > enrichmentApplied > existing.
      const body = Array.isArray(edits.body)
        ? edits.body.map((p) => String(p || '').trim()).filter(Boolean)
        : (enrichmentApplied?.intro_body?.length
            ? enrichmentApplied.intro_body
            : (existing.body || []));
      const next = {
        ...existing,
        body, // garantit body non-vide pour que le mapper utilise intro_letter
        ...(typeof edits.greeting === 'string' ? { greeting: edits.greeting.trim() } : {}),
        ...(typeof edits.signature === 'string' ? { signature: edits.signature.trim() } : {}),
        ...(typeof edits.pull_quote === 'string'
            ? { pull_quote: edits.pull_quote.trim() }
            : {}),
      };
      const updated = await saveEditorialConsultation({
        ...localConsultation,
        intro_letter: next,
      });
      setLocalConsultation(updated);
      setEditsSavedAt(Date.now());
      // Cache l'indicateur "Enregistre" apres 2.5s
      setTimeout(() => setEditsSavedAt((v) => (v && Date.now() - v >= 2400 ? null : v)), 2500);
    } catch (err) {
      setEditsError(err?.message || String(err));
    } finally {
      setSavingEdits(false);
    }
  };

  const workspaceHeaderActions = (
    <>
      {onOpenMessages && (
        <button
          type="button"
          className="delivery-workspace__action"
          onClick={async () => {
            const closed = await handleWorkspaceClose();
            if (closed !== false) onOpenMessages();
          }}
        >
          <Icon type="message" size={14} />
          Messages
        </button>
      )}
      <button
        type="button"
        className="delivery-workspace__action"
        onClick={handleWorkspaceClose}
      >
        ← Enregistrer et revenir à Livraison
      </button>
      <button
        type="button"
        className="delivery-workspace__action delivery-workspace__action--quiet"
        onClick={() => setTab(tab === 'sections' ? 'diagnostic' : 'sections')}
        title="Mode technique"
        aria-label="Afficher ou masquer le diagnostic technique"
      >
        <Icon type={tab === 'sections' ? 'gear' : 'search'} size={15} />
      </button>
    </>
  );

  return (
    <div
      className="modal-overlay delivery-workspace-overlay"
      onClick={() => { if (!modalBusy) handleWorkspaceClose(); }}
    >
      <div
        ref={panelRef}
        className="modal-content delivery-workspace-panel"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Aperçu app cliente"
        aria-busy={modalBusy}
        onClick={(e) => e.stopPropagation()}
        style={{
          padding: 0,
          maxWidth: 1280,
          width: '94vw',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DeliveryFinalizationWorkspace
          clientName={(client?.prenom || client?.form?.prenom || 'Cliente').trim()}
          clientGreeting={plan?.sections?.intro_data?.greeting?.trim() || null}
          packLabel={plan?.client_meta?.pack_label || plan?.client_meta?.pack || 'Accompagnement nutritionnel'}
          model={deliveryWorkspaceModel}
          activeSection={activeWorkspaceSection}
          onNavigate={handleWorkspaceNavigate}
          onOpenFullPreview={handleWorkspaceFullPreview}
          fullPreviewOpen={activeWorkspaceSection === 'all'}
          preparationActive={activeWorkspaceSection === 'package'}
          onOpenPreparation={() => handleWorkspaceNavigate('package')}
          onClose={handleWorkspaceClose}
          headerActions={workspaceHeaderActions}
        >

        {tab !== 'sections' && (
          <div
            role="tablist"
            aria-label="Vue (mode dev)"
            style={{
              display: 'flex',
              gap: 4,
              padding: '10px 22px 0',
              borderBottom: '1px solid rgba(255,255,255,.06)',
              background: 'rgba(80, 30, 30, 0.10)',
            }}
          >
            <span style={{ alignSelf: 'center', marginRight: 12, fontSize: '0.7rem', color: '#a86060', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Icon type="gear" size={12} />
                Mode technique
              </span>
            </span>
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active}
                  type="button"
                  onClick={() => setTab(t.id)}
                  style={{
                    background: active ? 'rgba(212,201,168,.1)' : 'transparent',
                    border: 'none',
                    borderBottom: active ? '2px solid #d4c9a8' : '2px solid transparent',
                    color: active ? '#d4c9a8' : '#8a8a7a',
                    padding: '8px 14px',
                    fontSize: '.82rem',
                    fontWeight: active ? 600 : 400,
                    cursor: 'pointer',
                    marginBottom: -1,
                  }}
                >
                  {t.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={handleCopy}
              disabled={!plan}
              style={{
                marginLeft: 'auto',
                background: 'rgba(255,255,255,.05)',
                border: '1px solid rgba(255,255,255,.1)',
                color: '#d4c9a8',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: '.75rem',
                cursor: 'pointer',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {copied && <Icon type="check" size={12} />}
                {copied ? 'Copié' : 'Copier JSON'}
              </span>
            </button>
          </div>
        )}

        {/* Bloc proposition IA (visible UNIQUEMENT si l'IA a renvoyé un draft non encore accepté) */}
        {(enrichmentDraft || enrichmentError || enrichmentApplied) && (
          <EnrichmentBanner
            draft={enrichmentDraft}
            applied={enrichmentApplied}
            error={enrichmentError}
            onAccept={handleAcceptEnrichment}
            onReject={handleRejectEnrichment}
            onReset={handleResetEnrichment}
            onRegenerate={handleEnrich}
            regenerating={enriching}
            locked={editorialLocked}
          />
        )}

        {/* Corps — V97.13.34 : vue "page web cliente" pleine largeur.
            Plus de mockup iPhone — Anissa voit l intégralité de l expérience
            cliente comme une vraie page web premium (header chaleureux +
            sections grand format).
            Enveloppe dans une ErrorBoundary locale : un crash de rendu pendant
            la génération (stratégie / recettes / enrichissement) s'affiche ICI,
            dans la modale, sans démonter tout le cockpit (plus d'écran noir). */}
        <ErrorBoundary>
        <div className="jrn-app-preview-fullpage">
          {error && (
            <div
              style={{
                padding: 14,
                borderRadius: 8,
                background: 'rgba(220,80,80,.1)',
                border: '1px solid rgba(220,80,80,.3)',
                color: '#f5c6c6',
                fontSize: '.85rem',
                marginBottom: 12,
              }}
            >
              <strong>Erreur de mapping :</strong> {error}
            </div>
          )}

          {plan && tab === 'json' && (
            <pre
              style={{
                margin: 0,
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                fontSize: '.78rem',
                lineHeight: 1.5,
                color: '#cfcfc4',
                background: 'rgba(0,0,0,.25)',
                padding: 14,
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {JSON.stringify(plan, null, 2)}
            </pre>
          )}

          {plan && tab === 'sections' && (
            <ClientFullPagePreview
              client={client}
              plan={plan}
              showHero={false}
              onImprove={aiActionsAllowed && rawPlan && !editorialLocked ? handleEnrich : undefined}
              improving={enriching}
            >
              {() => (
                <SectionsOverview
                  plan={plan}
                  activeSection={activeWorkspaceSection}
                  onImproveSection={aiActionsAllowed && rawPlan && !editorialLocked ? handleEnrichForSection : undefined}
                  improving={enriching}
                  improvingSection={enrichmentTargetSection}
                  onEditIntro={editorialLocked ? undefined : handleSaveIntroEdits}
                  onEditStrategy={editorialLocked ? undefined : handleSaveStrategyEdits}
                  supplementSourceProtocols={supplementSourceProtocols}
                  supplementOverride={localConsultation?.editorial_overrides?.supplements}
                  onEditSupplements={editorialLocked ? undefined : handleSaveSupplementsEdits}
                  onEditFridge={editorialLocked ? undefined : handleSaveFridgeEdits}
                  onSaveWeekMenus={editorialLocked ? undefined : handleSaveWeekMenus}
                  weekMenusModel={weekMenusModel}
                  weekEditorAuthorityStale={weekEditorAuthorityStale}
                  menusAlreadyPublished={menusAlreadyPublished}
                  weekEditorReclaimsSource={weekEditorReclaimsSource}
                  weekMenusWeekMeals={publicationConsultation === localConsultation ? rawPlan?.sections?.week_meals || null : null}
                  onOpenRotation={() => handleWorkspaceNavigate('rotation')}
                  client={client}
                  consultation={localConsultation}
                  clientAppPackageDirectives={localConsultation?.aiDirectives || localConsultation?.ai_directives || ''}
                  onSaveClientAppPackage={editorialLocked ? undefined : saveEditorialConsultation}
                  onClientAppPackageApplied={setLocalConsultation}
                  onCloseWorkspace={onClose}
                  onRegisterWorkspaceSaveAndClose={(handler) => { workspaceSaveAndCloseRef.current = handler; }}
                  onOpenRecipes={() => handleWorkspaceNavigate('week')}
                  fourWeekRotationValue={localConsultation?.editorial_overrides?.four_week_rotation}
                  onSaveFourWeekRotation={editorialLocked ? undefined : handleSaveFourWeekRotation}
                  mealPrepValue={localConsultation?.editorial_overrides?.meal_prep}
                  onSaveMealPrep={editorialLocked ? undefined : handleSaveMealPrep}
                  mealPrepV2Value={localConsultation?.editorial_overrides?.meal_prep_v2}
                  onSaveMealPrepV2={editorialLocked ? undefined : handleSaveMealPrepV2}
                  fridgeSyncValue={localConsultation?.editorial_overrides?.fridge_sync}
                  onSaveFridgeSync={editorialLocked ? undefined : handleSaveFridgeSync}
                  onMealPrepDirtyChange={editorialLocked ? undefined : setMealPrepDirty}
                  onSectionDirtyChange={editorialLocked ? undefined : handleSectionDirtyChange}
                  savingEdits={savingEdits || editorialLocked}
                  editsSavedAt={editsSavedAt}
                  editsError={editsError}
                />
              )}
            </ClientFullPagePreview>
          )}

          {plan && tab === 'diagnostic' && diag && <DiagnosticView diag={diag} />}
        </div>
        </ErrorBoundary>

        {publishAllowed && <>
          {/* La préparation éditoriale peut être ouverte avant la remise du
              Word. Dans ce mode, aucune action ni mention de publication ne
              doit détourner Anissa de la préparation des recettes. */}
          <RepublicationNoticeBlock statusEntry={statusEntry} />

          {/* Exclusions écrites en prose : relecture des textes rédigés juste
              avant « Publier », là où Anissa voit l'introduction, la stratégie
              et les compléments tels qu'ils partent dans l'app. */}
          {narrativeProseReview?.required && (
            <label className="narrative-prose-review" style={{ display: 'flex', alignItems: 'flex-start', gap: 9, minHeight: 44, margin: '12px 0', color: narrativeProseReview.confirmed ? '#8abf9a' : '#cfcfc4', fontSize: '.8rem', lineHeight: 1.45 }}>
              <input
                type="checkbox"
                checked={narrativeProseReview.confirmed}
                disabled={narrativeProseReview.confirmed || savingEdits || editorialLocked}
                onChange={handleConfirmNarrativeProseReview}
                style={{ marginTop: 2, minWidth: 18, minHeight: 18 }}
              />
              <span>
                {narrativeProseReview.label}
                {narrativeProseReview.confirmed ? ' — confirmé pour ces textes.' : ' (exigé avant de publier pour un programme MGD).'}
              </span>
            </label>
          )}

          <PublishFooter
          cfgCheck={cfgCheck}
          clientEmail={clientEmail}
          plan={plan}
          publishing={publishing}
          publishResult={effectivePublishResult}
          optionsWarning={optionsWarning}
          publishError={publishError}
          confirming={confirmingPublish}
          publishAllowed={publishAllowed}
          followupScheduleValid={resolveFollowupPublicationWeek(publicationConsultation, client) !== null}
          editorialIssue={editorialPublicationIssue}
          completionPending={effectiveCompletionPending}
          completingPublication={completingPublication}
          publicationCompletionRequired={effectivePublicationCompletionRequired}
          publicationRecoveryState={publicationRecoveryState}
          followupWeek={resolveFollowupPublicationWeek(publicationConsultation, client) || 0}
          phaseTransitionConfirmationRequired={Boolean(phaseTransitionRun)}
          onAskConfirm={() => { setConfirmingPublish(true); setPublishError(null); }}
          onCancel={() => setConfirmingPublish(false)}
          onConfirm={handlePublish}
          onRetryCompletion={handleRetryCompletion}
          onRetryPublicationStatus={() => setStatusRefreshNonce((value) => value + 1)}
          />
        </>}
        </DeliveryFinalizationWorkspace>
      </div>
    </div>
  );
}

// ─── Footer publication ──────────────────────────────────────────────────
//
// Affiche : config/cliente check → bouton "Publier" → confirm → status →
// résultat avec lien "Voir dans l'app".

export function canPublishFromPreview(publishAllowed) {
  return publishAllowed === true;
}

export function canPublishAfterRecoveryCheck(completionRequired, recoveryState) {
  return completionRequired !== true || recoveryState === 'ready';
}

export async function executePreviewPublication(publishAllowed, publishFn) {
  if (!canPublishFromPreview(publishAllowed)) {
    return { allowed: false, result: null };
  }
  return { allowed: true, result: await publishFn() };
}

export async function completePublishedJourney(onPublished, publishResult) {
  if (typeof onPublished === 'function') {
    return onPublished(publishResult);
  }
  return null;
}

export function recoverPublishedJourneyResult({
  statusEntry,
  consultationId,
  completionRequired,
} = {}) {
  const exactConsultationId = String(consultationId || '').trim();
  if (completionRequired !== true || !exactConsultationId) return null;
  if (statusEntry?.found !== true
      || statusEntry?.account_enabled !== true
      || statusEntry?.account_deleted === true
      || statusEntry?.plan_visible !== true
      || statusEntry?.visible_now !== true
      || statusEntry?.plan_status !== 'published'
      || String(statusEntry?.source_consultation_id || '') !== exactConsultationId
      || typeof statusEntry?.client_id !== 'string'
      || !statusEntry.client_id.trim()
      || typeof statusEntry?.plan_id !== 'string'
      || !statusEntry.plan_id.trim()
      || !Number.isInteger(statusEntry?.published_version)
      || statusEntry.published_version < 1
      || typeof statusEntry?.published_at !== 'string'
      || !statusEntry.published_at.trim()) {
    return null;
  }
  return {
    ok: true,
    recovered: true,
    client_id: statusEntry.client_id.trim(),
    plan_id: statusEntry.plan_id.trim(),
    status: 'published',
    published_version: statusEntry.published_version,
    published_at: statusEntry.published_at,
    effective_at: statusEntry.effective_at || null,
  };
}

export function getPublishBlockedReasons({
  plan,
  cfgCheck,
  clientEmail,
  publishAllowed,
  followupScheduleValid = true,
  mealPrepIssue = null,
  editorialIssue = null,
  publicationCompletionRequired = false,
  publicationRecoveryState = 'ready',
}) {
  const blockedReasons = [];
  if (!canPublishFromPreview(publishAllowed)) blockedReasons.push('Publication disponible depuis l’étape Livraison');
  if (!plan) blockedReasons.push('Mapping en erreur');
  if (!cfgCheck?.ok) blockedReasons.push('Config publication manquante');
  if (!clientEmail) blockedReasons.push('Cliente sans email');
  if (!canPublishAfterRecoveryCheck(publicationCompletionRequired, publicationRecoveryState)) {
    blockedReasons.push(publicationRecoveryState === 'error'
      ? 'Vérification de la publication indisponible'
      : 'Vérification de la publication en cours');
  }
  if (!followupScheduleValid) blockedReasons.push('Semaine de suivi manquante');
  if (editorialIssue || mealPrepIssue) blockedReasons.push(editorialIssue || mealPrepIssue);
  return blockedReasons;
}

function PublishFooter({
  cfgCheck, clientEmail, plan,
  publishing, publishResult, publishError, confirming, optionsWarning = null,
  publishAllowed = false,
  followupScheduleValid = true,
  mealPrepIssue = null,
  editorialIssue = null,
  completionPending = false,
  completingPublication = false,
  publicationCompletionRequired = false,
  publicationRecoveryState = 'ready',
  followupWeek = 0,
  phaseTransitionConfirmationRequired = false,
  onAskConfirm, onCancel, onConfirm, onRetryCompletion, onRetryPublicationStatus,
}) {
  const blockedReasons = getPublishBlockedReasons({
    plan,
    cfgCheck,
    clientEmail,
    publishAllowed,
    followupScheduleValid,
    mealPrepIssue,
    editorialIssue,
    publicationCompletionRequired,
    publicationRecoveryState,
  });

  // V96.0 — message de gating temporel selon followupWeek
  // 0 = plan initial → visible tout de suite
  // 1 = S4, 2 = S8, 3 = S12, 4 = S16 → visible N×4 semaines après le plan initial
  const isFollowup = followupWeek > 0;
  const followupLabel = isFollowup ? `S${followupWeek * 4}` : null;
  const weeksAfterInitial = followupWeek * 4;

  const canPublish = blockedReasons.length === 0 && !publishing;

  return (
    <footer
      style={{
        borderTop: '1px solid rgba(255,255,255,.08)',
        padding: '14px 22px',
        background: 'rgba(0,0,0,.15)',
      }}
    >
      {/* État succès */}
      {publishResult?.ok && (
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            background: 'rgba(80,160,100,.1)',
            border: '1px solid rgba(80,160,100,.3)',
            color: '#b6d8b8',
            fontSize: '.85rem',
            marginBottom: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon type="check" size={14} />
            <span>{publishResult.staged
              ? 'Livrable préparé — activation sécurisée en cours'
              : `Plan publié — version ${publishResult.published_version}`}</span>
          </div>
          {/* V96.0 : si effective_at futur, indiquer la date à laquelle la
              cliente verra ce plan. Sinon "visible immédiatement". */}
          {publishResult.effective_at && (
            (() => {
              const eff = new Date(publishResult.effective_at);
              const now = new Date();
              const diffDays = Math.round((eff.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
              if (diffDays > 0) {
                return (
                  <div style={{ fontSize: '.78rem', color: '#e5c878' }}>
                    Visible par la cliente le {eff.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} (dans {diffDays} jours).
                  </div>
                );
              }
              return null;
            })()
          )}
          {!publishResult.staged && (
            <div style={{ fontSize: '.75rem', color: '#8eb892' }}>
              La cliente peut se connecter avec <strong>{clientEmail}</strong>.
            </div>
          )}
          {optionsWarning && (
            <div
              role="status"
              style={{
                marginTop: 2,
                padding: '8px 10px',
                borderRadius: 7,
                color: '#f0d994',
                background: 'rgba(224,180,70,.10)',
                border: '1px solid rgba(224,180,70,.28)',
                fontSize: '.82rem',
                lineHeight: 1.5,
              }}
            >
              Publication réussie. Les options de l’espace cliente n’ont pas pu être
              appliquées automatiquement : recoche le suivi du poids ou les photos de
              repas depuis Livraison. ({optionsWarning})
            </div>
          )}
          {publishResult.saas_publication_stamp_warning && (
            <div
              role="status"
              style={{
                marginTop: 2,
                padding: '8px 10px',
                borderRadius: 7,
                color: '#f0d994',
                background: 'rgba(224,180,70,.10)',
                border: '1px solid rgba(224,180,70,.28)',
                fontSize: '.76rem',
                lineHeight: 1.45,
              }}
            >
              Publication réussie. Le repère SaaS de cette version reste à confirmer : recharge la fiche avant toute nouvelle modification.
            </div>
          )}
          {!publishResult.staged && publishResult.login_url && (
            <a
              href={publishResult.login_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#b6d8b8',
                fontSize: '.78rem',
                textDecoration: 'underline',
              }}
            >
              Ouvrir l'app cliente ({publishResult.login_url})
            </a>
          )}
        </div>
      )}

      {/* État erreur */}
      {publishError && (
        <div
          style={{
            padding: 12,
            borderRadius: 8,
            background: 'rgba(220,80,80,.1)',
            border: '1px solid rgba(220,80,80,.3)',
            color: '#f5c6c6',
            fontSize: '.82rem',
            marginBottom: 10,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon type="warning" size={14} />
            {publishError}
          </span>
        </div>
      )}

      {/* Raisons bloquantes */}
      {blockedReasons.length > 0 && (
        <div
          style={{
            padding: 10,
            borderRadius: 6,
            background: 'rgba(220,180,80,.08)',
            border: '1px solid rgba(220,180,80,.25)',
            color: '#e5c878',
            fontSize: '.78rem',
            marginBottom: 10,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon type="warning" size={14} />
            Publication bloquée : {blockedReasons.join(' • ')}
          </span>
          {!cfgCheck.ok && cfgCheck.issues.length > 0 && (
            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
              {cfgCheck.issues.map((iss, i) => (
                <li key={i}>{iss}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Bouton confirm OU bouton publier */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {publicationCompletionRequired && publicationRecoveryState === 'error' && (
          <button
            type="button"
            onClick={onRetryPublicationStatus}
            style={{
              background: 'transparent',
              border: '1px solid rgba(229,200,120,.55)',
              color: '#e5c878',
              padding: '8px 16px',
              borderRadius: 6,
              fontSize: '.82rem',
              cursor: 'pointer',
              marginRight: 'auto',
            }}
          >
            Réessayer la vérification
          </button>
        )}
        {completionPending ? (
          <button
            type="button"
            onClick={onRetryCompletion}
            disabled={completingPublication}
            style={{
              background: '#2E4E38',
              border: '1px solid #2E4E38',
              color: '#FAF9F6',
              padding: '8px 18px',
              borderRadius: 6,
              fontSize: '.82rem',
              fontWeight: 600,
              cursor: completingPublication ? 'wait' : 'pointer',
              opacity: completingPublication ? 0.6 : 1,
            }}
          >
            {completingPublication
              ? 'Finalisation…'
              : phaseTransitionConfirmationRequired
                ? 'Finaliser l’activation de la phase'
                : 'Finaliser le passage au Suivi'}
          </button>
        ) : confirming ? (
          <>
            <span style={{ fontSize: '.78rem', color: '#cfcfc4', marginRight: 'auto', flex: '1 1 auto', minWidth: 200 }}>
              {isFollowup ? (
                <>
                  Plan <strong>{followupLabel}</strong> de <strong>{clientEmail}</strong> — visible {weeksAfterInitial} semaines après le plan initial.
                </>
              ) : (
                <>
                  {phaseTransitionConfirmationRequired
                    ? <>Je confirme ce livrable exact pour <strong>{clientEmail}</strong>. Il sera lié à cette transition avant publication.</>
                    : <>Publier vers <strong>{clientEmail}</strong> ? Visible immédiatement.</>}
                  {publishResult?.ok && ' (Ré-publication — nouvelle version, ancienne archivée.)'}
                </>
              )}
            </span>
            <button
              type="button"
              onClick={onCancel}
              disabled={publishing}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,.15)',
                color: '#8a8a7a',
                padding: '8px 16px',
                borderRadius: 6,
                fontSize: '.82rem',
                cursor: 'pointer',
              }}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => onConfirm()}
              disabled={publishing}
              style={{
                background: '#2E4E38',
                border: '1px solid #2E4E38',
                color: '#FAF9F6',
                padding: '8px 18px',
                borderRadius: 6,
                fontSize: '.82rem',
                fontWeight: 600,
                cursor: publishing ? 'wait' : 'pointer',
                opacity: publishing ? 0.6 : 1,
              }}
            >
              {publishing
                ? 'Publication…'
                : isFollowup
                  ? 'Programmer la publication'
                  : phaseTransitionConfirmationRequired
                    ? 'Confirmer ce livrable et publier'
                    : 'Confirmer'}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onAskConfirm}
            disabled={!canPublish}
            style={{
              background: canPublish ? '#2E4E38' : 'rgba(255,255,255,.05)',
              border: `1px solid ${canPublish ? '#2E4E38' : 'rgba(255,255,255,.1)'}`,
              color: canPublish ? '#FAF9F6' : '#666',
              padding: '8px 18px',
              borderRadius: 6,
              fontSize: '.85rem',
              fontWeight: 600,
              cursor: canPublish ? 'pointer' : 'not-allowed',
            }}
            title={canPublish ? 'Publier le programme dans l\'app cliente' : blockedReasons.join(' • ')}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon type={publishResult?.ok ? "refresh" : "arrow-right"} size={14} />
              {publishResult?.ok ? 'Re-publier' : 'Publier le programme'}
            </span>
          </button>
        )}
      </div>
    </footer>
  );
}

// ─── Aperçu sections (vue lisible) ──────────────────────────────────────

// V97.13.28 — Refonte UX premium :
// Au lieu de compteurs techniques + "manquants post-migration", on affiche
// les vraies données de chaque section de l'app cliente, en formatage
// clinique premium. Anissa voit immédiatement ce que Camille verra.
//
// Pas d'onglets techniques visibles, pas de jargon. Vue verticale claire.
function SectionsOverview({ plan, activeSection = 'package', onImproveSection, improving = false, improvingSection = null, onEditIntro, onEditStrategy, supplementSourceProtocols = null, supplementOverride = null, onEditSupplements, onEditFridge, onSaveWeekMenus, weekMenusModel = null, weekMenusWeekMeals = null, onOpenRotation, weekEditorAuthorityStale = false, menusAlreadyPublished = false, weekEditorReclaimsSource = false, client = null, consultation = null, clientAppPackageDirectives = '', onSaveClientAppPackage, onClientAppPackageApplied, onCloseWorkspace, onRegisterWorkspaceSaveAndClose, onOpenRecipes, fourWeekRotationValue = null, onSaveFourWeekRotation, mealPrepValue = null, onSaveMealPrep, mealPrepV2Value = null, onSaveMealPrepV2, fridgeSyncValue = null, onSaveFridgeSync, onMealPrepDirtyChange, onSectionDirtyChange, savingEdits = false, editsSavedAt = null, editsError = null }) {
  // V97.13.45 — onImproveSection(key) cible une section specifique au lieu
  // du global onImprove. Chaque card recoit son propre handler.
  const onImproveIntro = onImproveSection ? () => onImproveSection('intro') : undefined;
  const onImproveStrategy = onImproveSection ? () => onImproveSection('strategy') : undefined;
  const onImproveFridge = onImproveSection ? () => onImproveSection('fridge') : undefined;
  const onImproveProtocols = onImproveSection ? () => onImproveSection('protocols') : undefined;
  const s = plan.sections || {};
  const showSection = (sectionId) => activeSection === 'all' || activeSection === sectionId;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {activeSection === 'package' && (
        <section id="delivery-section-package" className="delivery-workspace__anchor" tabIndex={-1}>
          <ClientAppWorkspace
            client={client}
            consultation={consultation}
            directives={clientAppPackageDirectives}
            onSave={onSaveClientAppPackage}
            onApplied={onClientAppPackageApplied}
            onClose={onCloseWorkspace}
            onRegisterSaveAndClose={onRegisterWorkspaceSaveAndClose}
            disabled={savingEdits}
          />
        </section>
      )}
      {/* ─── Lettre d'intro ────────────────────────────────────────── */}
      {showSection('introduction') && <section id="delivery-section-introduction" className="delivery-workspace__anchor" tabIndex={-1}>
        <IntroCardEditable
          intro={s.intro_data}
          onImprove={onImproveIntro}
          improving={improving && improvingSection === 'intro'}
          onSave={onEditIntro}
          onDirtyChange={onSectionDirtyChange}
          saving={savingEdits}
          savedAt={editsSavedAt}
          error={editsError}
        />
      </section>}

      {/* ─── Stratégie & piliers (A3a éditable) ─────────────────── */}
      {showSection('strategy') && <section id="delivery-section-strategy" className="delivery-workspace__anchor" tabIndex={-1}>
        <StrategyCardEditable
          strategy={s.strategy_data}
          onImprove={onImproveStrategy}
          improving={improving && improvingSection === 'strategy'}
          onSave={onEditStrategy}
          onDirtyChange={onSectionDirtyChange}
          saving={savingEdits}
          savedAt={editsSavedAt}
          error={editsError}
        />
      </section>}

      {/* ─── Semaine, menus & recettes : 4 semaines, un réservoir par repas ──── */}
      {(activeSection === 'all' || activeSection === 'week') && <section id="delivery-section-week" className="delivery-workspace__anchor" tabIndex={-1} style={{ display: 'grid', gap: 16 }}>
        <WeekMenusEditor
          client={client}
          consultation={consultation}
          weekMeals={weekMenusWeekMeals}
          onSave={onSaveWeekMenus}
          onDirtyChange={onSectionDirtyChange}
          onOpenRotation={onOpenRotation}
          disabled={savingEdits}
          authorityStale={weekEditorAuthorityStale}
          alreadyPublished={menusAlreadyPublished}
          reclaimsSource={weekEditorReclaimsSource}
        />
        <WeekMealsPreviewCard weekMeals={s.week_meals} />
      </section>}

      {showSection('rotation') && <section id="delivery-section-rotation" className="delivery-workspace__anchor" tabIndex={-1}>
        <FourWeekRotationEditorialCard
          weekMeals={s.week_meals}
          value={fourWeekRotationValue}
          weekMenus={weekMenusModel}
          onSave={onSaveFourWeekRotation}
          onDirtyChange={onSectionDirtyChange}
          onOpenRecipes={onOpenRecipes}
        />
      </section>}

      {showSection('meal-prep') && <section id="delivery-section-meal-prep" className="delivery-workspace__anchor" tabIndex={-1}>
        {canUseModularMealPrep(s.week_meals) ? (
          <ModularMealPrepEditorialCard
            weekMeals={s.week_meals}
            value={mealPrepV2Value}
            onSave={onSaveMealPrepV2}
            onDirtyChange={onMealPrepDirtyChange}
            compactReview={activeSection !== 'all'}
          />
        ) : (
          <MealPrepEditorialCard
            weekMeals={s.week_meals}
            value={mealPrepValue}
            onSave={onSaveMealPrep}
            onDirtyChange={onMealPrepDirtyChange}
          />
        )}
      </section>}

      {/* ─── Frigo & courses (A3c — éditable) ───────────────────── */}
      {showSection('fridge') && <section id="delivery-section-fridge" className="delivery-workspace__anchor" tabIndex={-1}>
        <FridgeCardEditable
          fridge={s.fridge_data}
          weekMeals={s.week_meals}
          mealPrepValue={mealPrepValue}
          fridgeSyncValue={fridgeSyncValue}
          onSaveFridgeSync={onSaveFridgeSync}
          onImprove={onImproveFridge}
          improving={improving && improvingSection === 'fridge'}
          onSave={onEditFridge}
          onDirtyChange={onSectionDirtyChange}
          saving={savingEdits}
          savedAt={editsSavedAt}
          error={editsError}
          compactReview={activeSection !== 'all'}
        />
      </section>}

      {/* ─── Compléments (A3b — intro éditable) ─────────────────── */}
      {showSection('supplements') && <section id="delivery-section-supplements" className="delivery-workspace__anchor" tabIndex={-1}>
        <SupplementsCardEditable
          protocols={s.protocols_data}
          sourceProtocols={supplementSourceProtocols}
          supplementOverride={supplementOverride}
          onImprove={onImproveProtocols}
          improving={improving && improvingSection === 'protocols'}
          onSave={onEditSupplements}
          onDirtyChange={onSectionDirtyChange}
          saving={savingEdits}
          savedAt={editsSavedAt}
          error={editsError}
        />
      </section>}

      {/* ─── Rotation (compact si vide) ───────────────────────────── */}
      {showSection('rotation') && s.rotation_data?.categories?.length > 0 && (
        <CapsCard
          iconType="refresh"
          title="Rotation alimentaire"
          subtitle={`${s.rotation_data.categories.length} catégories`}
          empty={false}
        >
          {s.rotation_data.categories.map((c) => (
            <div key={c.id} style={{ marginBottom: 6 }}>
              <span style={capsSubTitleStyle}>{c.title}{c.primary ? ' · prioritaire' : ''}</span>
              <span style={{ color: '#8a8a7a', fontSize: '.78rem', marginLeft: 8 }}>
                {c.items?.length || 0} alternatives
              </span>
            </div>
          ))}
        </CapsCard>
      )}
    </div>
  );
}

// ─── Card premium pour l'aperçu Anissa ────────────────────────────────
function CapsCard({ iconType, title, subtitle, empty, emptyLabel, children, onImprove, improving = false, improveLabel }) {
  return (
    <div style={capsCardStyle}>
      <header style={capsCardHeaderStyle}>
        <Icon type={iconType || "doc"} size={18} style={{ color: '#d4c9a8' }} />
        <div style={{ flex: 1 }}>
          <h4 style={capsCardTitleStyle}>{title}</h4>
          {subtitle && <p style={capsCardSubtitleStyle}>{subtitle}</p>}
        </div>
        {empty && <span style={capsCardBadgeStyle}>à enrichir</span>}
        {onImprove && (
          <button
            type="button"
            onClick={onImprove}
            disabled={improving}
            style={{
              background: 'rgba(120, 80, 200, 0.08)',
              border: '1px solid rgba(180, 140, 255, 0.25)',
              color: '#b89eff',
              padding: '5px 11px',
              borderRadius: 6,
              fontSize: '.75rem',
              fontWeight: 600,
              cursor: improving ? 'wait' : 'pointer',
              opacity: improving ? 0.6 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
            title="Préparer une nouvelle proposition pour cette section"
          >
            <Icon type="pencil" size={13} />
            <span>{improving ? 'Préparation…' : (improveLabel || 'Améliorer')}</span>
          </button>
        )}
      </header>
      <div style={capsCardBodyStyle}>
        {empty ? (
          <p style={capsEmptyStyle}>{emptyLabel}</p>
        ) : children}
      </div>
    </div>
  );
}

// ─── Styles premium app cliente preview ──────────────────────────────
const capsCardStyle = {
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 12,
  padding: 0,
  overflow: 'hidden',
};
const capsCardHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '12px 16px',
  background: 'rgba(212, 201, 168, 0.04)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
};
const capsCardTitleStyle = {
  margin: 0,
  color: '#d4c9a8',
  fontSize: '0.92rem',
  fontWeight: 600,
  letterSpacing: '-0.005em',
};
const capsCardSubtitleStyle = {
  margin: '2px 0 0',
  color: '#8a8a7a',
  fontSize: '0.74rem',
};
const capsCardBadgeStyle = {
  padding: '3px 10px',
  background: 'rgba(220, 180, 80, 0.12)',
  border: '1px solid rgba(220, 180, 80, 0.30)',
  borderRadius: 999,
  color: '#e5c878',
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};
const capsCardBodyStyle = {
  padding: '14px 16px',
};
const capsEmptyStyle = {
  margin: 0,
  color: '#666',
  fontSize: '0.82rem',
  fontStyle: 'italic',
};
// Intro
const capsGreetingStyle = {
  margin: '0 0 10px',
  color: '#d4c9a8',
  fontSize: '1rem',
  fontWeight: 500,
  fontStyle: 'italic',
  letterSpacing: '-0.005em',
};
const capsBodyStyle = {
  margin: '0 0 8px',
  color: '#cfcfc4',
  fontSize: '0.86rem',
  lineHeight: 1.55,
};
const capsQuoteStyle = {
  margin: '12px 0 0',
  padding: '10px 14px',
  borderLeft: '3px solid #d4c9a8',
  background: 'rgba(212, 201, 168, 0.04)',
  color: '#d4c9a8',
  fontSize: '0.88rem',
  fontStyle: 'italic',
  lineHeight: 1.5,
};
const capsPointTitleStyle = {
  fontFamily: 'system-ui',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#d4c9a8',
  marginBottom: 3,
};
const capsPointDetailStyle = {
  color: '#cfcfc4',
  fontSize: '0.82rem',
  lineHeight: 1.5,
};
const capsSignatureStyle = {
  margin: '12px 0 0',
  color: '#8a8a7a',
  fontSize: '0.84rem',
  fontStyle: 'italic',
};
// Stratégie
const capsPillarStyle = {
  padding: 12,
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: 8,
};
const capsPillarTitleStyle = {
  color: '#d4c9a8',
  fontSize: '0.86rem',
  fontWeight: 600,
  marginBottom: 4,
};
const capsPillarDescStyle = {
  color: '#cfcfc4',
  fontSize: '0.8rem',
  lineHeight: 1.5,
};
const capsSubTitleStyle = {
  color: '#d4c9a8',
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 6,
};
const capsListStyle = {
  margin: '4px 0 0',
  padding: 0,
  listStyle: 'none',
};
const capsListItemStyle = {
  padding: '4px 0 4px 18px',
  position: 'relative',
  color: '#cfcfc4',
  fontSize: '0.82rem',
  lineHeight: 1.45,
};
// Semaine
const capsMealStyle = {
  padding: '10px 12px',
  background: 'rgba(255, 255, 255, 0.03)',
  borderLeft: '2px solid #d4c9a8',
  borderRadius: '0 6px 6px 0',
};
const capsMealSlotStyle = {
  color: '#d4c9a8',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  marginBottom: 3,
};
const capsMealTitleStyle = {
  color: '#cfcfc4',
  fontSize: '0.85rem',
  lineHeight: 1.45,
};
const capsMealAltsStyle = {
  marginTop: 4,
  color: '#9b9b89',
  fontSize: '0.72rem',
};
const capsMealRecipeStyle = {
  marginTop: 4,
  color: '#a8e890',
  fontSize: '0.72rem',
  fontWeight: 600,
};
// Frigo
const capsPillsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
};
const capsPillStyle = {
  padding: '4px 10px',
  background: 'rgba(212, 201, 168, 0.08)',
  border: '1px solid rgba(212, 201, 168, 0.18)',
  borderRadius: 999,
  color: '#d4c9a8',
  fontSize: '0.76rem',
};
const capsPillLimitStyle = {
  background: 'rgba(220, 140, 80, 0.06)',
  border: '1px solid rgba(220, 140, 80, 0.20)',
  color: '#e3a878',
};
// Compléments
const capsCompStyle = {
  padding: '10px 12px',
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: 8,
};
const capsCompNameStyle = {
  color: '#d4c9a8',
  fontSize: '0.84rem',
  fontWeight: 600,
  marginBottom: 4,
};
const capsCompMetaStyle = {
  color: '#9b9b89',
  fontSize: '0.74rem',
  marginTop: 2,
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  lineHeight: 1.45,
};
const capsCompBenefitStyle = {
  marginTop: 6,
  paddingTop: 6,
  borderTop: '1px dashed rgba(255, 255, 255, 0.08)',
  color: '#cfcfc4',
  fontSize: '0.78rem',
  fontStyle: 'italic',
  lineHeight: 1.4,
};

function Meta({ plan }) {
  return (
    <div
      style={{
        padding: '10px 14px',
        background: 'rgba(212,201,168,.06)',
        border: '1px solid rgba(212,201,168,.15)',
        borderRadius: 8,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 18,
        fontSize: '.78rem',
      }}
    >
      <span style={{ color: '#8a8a7a' }}>
        Locale : <strong style={{ color: '#d4c9a8' }}>{plan.locale}</strong>
      </span>
      <span style={{ color: '#8a8a7a' }}>
        Mode : <strong style={{ color: '#d4c9a8' }}>{plan.mode}</strong>
      </span>
      <span style={{ color: '#8a8a7a' }}>
        Status : <strong style={{ color: '#d4c9a8' }}>{plan.status}</strong>
      </span>
      {plan.objective && (
        <span style={{ color: '#8a8a7a' }}>
          Objectif : <strong style={{ color: '#d4c9a8' }}>{plan.objective}</strong>
        </span>
      )}
    </div>
  );
}

function SectionCard({ title, empty, children }) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 8,
        background: empty ? 'rgba(220,180,80,.05)' : 'rgba(255,255,255,.03)',
        border: `1px solid ${empty ? 'rgba(220,180,80,.25)' : 'rgba(255,255,255,.08)'}`,
      }}
    >
      <h4
        style={{
          margin: '0 0 10px',
          color: empty ? '#e5c878' : '#d4c9a8',
          fontSize: '.92rem',
          fontWeight: 600,
        }}
      >
        {title}
        {empty && (
          <span style={{ marginLeft: 8, fontSize: '.7rem', fontWeight: 400 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon type="warning" size={11} />
              vide
            </span>
          </span>
        )}
      </h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </div>
  );
}

function KV({ label, value, indent }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        fontSize: '.78rem',
        paddingLeft: indent ? 8 : 0,
      }}
    >
      <span style={{ color: '#8a8a7a', minWidth: 160 }}>{label}</span>
      <span style={{ color: value ? '#cfcfc4' : '#666', fontStyle: value ? 'normal' : 'italic' }}>
        {value || '—'}
      </span>
    </div>
  );
}

function Missing({ fields }) {
  const missing = fields.filter(([, v]) => !v).map(([k]) => k);
  if (!missing.length) return null;
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: '.72rem',
        color: '#8a8a7a',
        fontStyle: 'italic',
      }}
    >
      Manquants (post-migration) : {missing.join(', ')}
    </div>
  );
}

function Note({ children }) {
  return (
    <div
      style={{
        marginTop: 8,
        padding: '6px 10px',
        background: 'rgba(80,140,200,.08)',
        border: '1px solid rgba(80,140,200,.2)',
        borderRadius: 6,
        fontSize: '.74rem',
        color: '#9bbed8',
      }}
    >
      {children}
    </div>
  );
}

// ─── Banner enrichissement IA ────────────────────────────────────────────

export function EnrichmentBanner({
  draft,
  applied,
  error,
  onAccept,
  onReject,
  onReset,
  onRegenerate,
  regenerating,
  locked = false,
}) {
  // Cas erreur
  if (error && !draft && !applied) {
    return (
      <div
        style={{
          margin: '0 22px 12px',
          padding: 12,
          borderRadius: 8,
          background: 'rgba(220,80,80,.08)',
          border: '1px solid rgba(220,80,80,.25)',
          color: '#f5c6c6',
          fontSize: '.82rem',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon type="warning" size={14} />
          Enrichissement impossible : {error}
        </span>
      </div>
    );
  }

  // Cas appliqué (Anissa a accepté)
  if (applied && !draft) {
    return (
      <div
        style={{
          margin: '0 22px 12px',
          padding: '10px 14px',
          borderRadius: 8,
          background: 'rgba(120,80,200,.1)',
          border: '1px solid rgba(180,140,255,.3)',
          color: '#cba8ff',
          fontSize: '.8rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon type="check" size={14} />
          Contenu accepté et enregistré. Modifie-le directement dans la section concernée si nécessaire.
        </span>
      </div>
    );
  }

  // Cas draft (proposition IA pas encore acceptée)
  if (!draft) return null;

  return (
    <div
      style={{
        margin: '0 22px 12px',
        padding: 14,
        borderRadius: 10,
        background: 'rgba(120,80,200,.08)',
        border: '1px solid rgba(180,140,255,.3)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div style={{ color: '#cba8ff', fontSize: '.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon type="doc" size={14} />
          Proposition à relire avant utilisation
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={onRegenerate} disabled={locked || regenerating} style={miniBtn('purple')}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="refresh" size={12} />Regénérer</span>
          </button>
          <button type="button" onClick={onReject} disabled={locked} style={miniBtn('ghost')}>
            Ignorer
          </button>
          <button type="button" onClick={onAccept} disabled={locked} style={miniBtn('purpleFilled')}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="check" size={12} />Utiliser</span>
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: '.82rem' }}>
        {draft.intro_body?.length > 0 && (
          <EnrichBlock label="Intro narrative">
            {draft.intro_body.map((p, i) => (
              <p key={i} style={{ margin: '0 0 6px', color: '#e5dfd0', lineHeight: 1.5 }}>
                {p}
              </p>
            ))}
          </EnrichBlock>
        )}

        {draft.pull_quote && (
          <EnrichBlock label="Pull quote">
            <p style={{ margin: 0, color: '#e5dfd0', fontStyle: 'italic', lineHeight: 1.5 }}>
              « {draft.pull_quote} »
            </p>
          </EnrichBlock>
        )}

        {draft.tailored_points?.length > 0 && (
          <EnrichBlock label="Points clés personnalisés">
            <ul style={{ margin: 0, padding: '0 0 0 16px', color: '#e5dfd0' }}>
              {draft.tailored_points.map((p) => (
                <li key={p.id} style={{ marginBottom: 4 }}>
                  <strong style={{ color: '#cba8ff' }}>{p.title}</strong> — {p.detail}
                </li>
              ))}
            </ul>
          </EnrichBlock>
        )}

        {draft.signature_phrase?.length > 0 && (
          <EnrichBlock label="Phrase signature (italique entre piliers et takeaways)">
            {draft.signature_phrase.map((s, i) => (
              <p key={i} style={{ margin: '0 0 4px', color: '#e5dfd0', fontStyle: 'italic' }}>
                {s}
              </p>
            ))}
          </EnrichBlock>
        )}

        {draft.section_intros && Object.keys(draft.section_intros).length > 0 && (
          <EnrichBlock label="Intros de section">
            {draft.section_intros.rotation && (
              <p style={{ margin: '0 0 4px', color: '#e5dfd0' }}>
                <strong style={{ color: '#cba8ff' }}>Rotation :</strong> {draft.section_intros.rotation}
              </p>
            )}
            {draft.section_intros.fridge && (
              <p style={{ margin: '0 0 4px', color: '#e5dfd0' }}>
                <strong style={{ color: '#cba8ff' }}>Frigo :</strong> {draft.section_intros.fridge}
              </p>
            )}
            {draft.section_intros.protocols && (
              <p style={{ margin: '0 0 4px', color: '#e5dfd0' }}>
                <strong style={{ color: '#cba8ff' }}>Compléments :</strong> {draft.section_intros.protocols}
              </p>
            )}
          </EnrichBlock>
        )}
      </div>
    </div>
  );
}

function EnrichBlock({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: '.68rem',
          fontWeight: 600,
          color: '#9d7cd8',
          textTransform: 'uppercase',
          letterSpacing: '.08em',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function miniBtn(variant) {
  const base = {
    padding: '5px 10px',
    borderRadius: 5,
    fontSize: '.74rem',
    cursor: 'pointer',
    fontWeight: 500,
  };
  if (variant === 'purpleFilled') {
    return {
      ...base,
      background: '#7d4fcf',
      border: '1px solid #7d4fcf',
      color: '#fff',
      fontWeight: 600,
    };
  }
  if (variant === 'purple') {
    return {
      ...base,
      background: 'rgba(120,80,200,.12)',
      border: '1px solid rgba(180,140,255,.25)',
      color: '#cba8ff',
    };
  }
  return {
    ...base,
    background: 'transparent',
    border: '1px solid rgba(255,255,255,.15)',
    color: '#8a8a7a',
  };
}

// ─── Diagnostic ──────────────────────────────────────────────────────────

function DiagnosticView({ diag }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: '.85rem' }}>
      <div
        style={{
          padding: 12,
          borderRadius: 8,
          background: diag.ok ? 'rgba(80,160,100,.1)' : 'rgba(220,180,80,.08)',
          border: `1px solid ${diag.ok ? 'rgba(80,160,100,.3)' : 'rgba(220,180,80,.3)'}`,
          color: diag.ok ? '#b6d8b8' : '#e5c878',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon type={diag.ok ? "check" : "warning"} size={14} />
          {diag.ok
            ? 'Toutes les sections principales contiennent des données.'
            : `${diag.issues.length} avertissement(s) — voir détails ci-dessous.`}
        </span>
      </div>

      <div>
        <h4 style={{ margin: '0 0 8px', color: '#d4c9a8', fontSize: '.85rem' }}>
          Compteurs
        </h4>
        <ul style={{ margin: 0, padding: '0 0 0 18px', color: '#cfcfc4' }}>
          <li>Intro — paragraphes : {diag.summary.intro_paragraphs}</li>
          <li>Stratégie — pillars : {diag.summary.strategy_pillars}, takeaways : {diag.summary.strategy_takeaways}</li>
          <li>Semaine — total repas : {diag.summary.week_total_meals}</li>
          <li>Rotation — catégories : {diag.summary.rotation_categories}</li>
          <li>Frigo — items : {diag.summary.fridge_items}</li>
          <li>Compléments — groupes : {diag.summary.protocol_groups}</li>
        </ul>
      </div>

      {diag.issues.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 8px', color: '#d4c9a8', fontSize: '.85rem' }}>
            Avertissements
          </h4>
          <ul style={{ margin: 0, padding: '0 0 0 18px', color: '#e5c878' }}>
            {diag.issues.map((iss, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{iss}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── V97.13.40 — Carte repas avec alternatives expansibles ────────────────
//
// Cohérent avec l'app cliente Next.js qui affiche "Autres options · 4 →" sur
// chaque repas. Avant V97.13.40 : la modal Aperçu n'affichait que le compteur
// sans détail. Anissa devait ouvrir la fiche frigo PDF pour les voir.
// Maintenant : click sur le bouton déroule la liste complète des alternatives
// dans la même surface visuelle.

function MealCardExpandable({ meal }) {
  const [expanded, setExpanded] = useState(false);
  const [recipeOpen, setRecipeOpen] = useState(false);
  const alts = meal?.alternatives || [];
  const hasAlts = alts.length > 0;
  const hasRecipe = !!(meal?.recipe?.ingredients?.length || meal?.recipe?.preparation?.length);

  return (
    <div style={capsMealStyle}>
      <div style={capsMealSlotStyle}>{meal.slot_label}</div>
      <div style={capsMealTitleStyle}>{meal.title}</div>

      {/* V97.13.46 — Recette principale du repas, expansible */}
      {hasRecipe && (
        <>
          <button
            type="button"
            onClick={() => setRecipeOpen((v) => !v)}
            style={{
              ...capsMealAltsStyle,
              background: 'transparent',
              border: 'none',
              padding: '6px 0 2px',
              cursor: 'pointer',
              color: '#e5b878',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '.78rem',
            }}
            title={recipeOpen ? 'Replier la recette' : 'Voir la recette'}
          >
            <Icon type="plate" size={14} />
            Recette détaillée
            <span style={{ transition: 'transform 140ms ease', transform: recipeOpen ? 'rotate(90deg)' : 'rotate(0)', display: 'inline-block' }}>→</span>
          </button>
          {recipeOpen && <RecipeDetail recipe={meal.recipe} />}
        </>
      )}

      {hasAlts && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            style={{
              ...capsMealAltsStyle,
              background: 'transparent',
              border: 'none',
              padding: '6px 0 2px',
              cursor: 'pointer',
              color: '#8abf9a',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '.78rem',
            }}
            title={expanded ? 'Replier les alternatives' : 'Voir les alternatives'}
          >
            Autres options · {alts.length}
            <span style={{ transition: 'transform 140ms ease', transform: expanded ? 'rotate(90deg)' : 'rotate(0)', display: 'inline-block' }}>→</span>
          </button>

          {expanded && (
            <ul style={{
              listStyle: 'none',
              margin: '8px 0 4px',
              padding: '0 0 0 12px',
              borderLeft: '2px solid rgba(106, 191, 138, 0.25)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}>
              {alts.map((alt, i) => {
                const altHasRecipe = !!(alt?.recipe?.ingredients?.length || alt?.recipe?.preparation?.length);
                return (
                  <li key={alt.id || i} style={{
                    fontSize: '.86rem',
                    color: '#d4c9a8',
                    lineHeight: 1.5,
                  }}>
                    <div>
                      <span style={{ color: '#8abf9a', marginRight: 6, fontWeight: 600 }}>{i + 1}.</span>
                      {alt.title || alt.name || alt.label || '—'}
                      {altHasRecipe && (
                        <span style={{ color: '#e5b878', marginLeft: 8, display: 'inline-flex', verticalAlign: 'middle' }} title="Recette détaillée disponible">
                          <Icon type="plate" size={12} />
                        </span>
                      )}
                    </div>
                    {alt.description && (
                      <div style={{ fontSize: '.78rem', color: '#8a8a7a', marginTop: 2, fontStyle: 'italic' }}>
                        {alt.description}
                      </div>
                    )}
                    {altHasRecipe && <RecipeDetail recipe={alt.recipe} compact />}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// Composant compact pour afficher une recette (ingrédients + préparation)
function RecipeDetail({ recipe, compact = false }) {
  if (!recipe) return null;
  const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
  const preparation = Array.isArray(recipe.preparation) ? recipe.preparation : [];
  return (
    <div style={{
      margin: compact ? '6px 0 2px' : '8px 0 4px',
      padding: '10px 12px',
      background: 'rgba(229, 184, 120, 0.05)',
      border: '1px solid rgba(229, 184, 120, 0.15)',
      borderRadius: 8,
      fontSize: compact ? '.78rem' : '.82rem',
    }}>
      {(recipe.prep_time_min || recipe.cook_time_min || recipe.servings) && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 8, fontSize: '.72rem', color: '#8a8a7a', flexWrap: 'wrap' }}>
          {recipe.prep_time_min ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon type="clock" size={12} />Prép. {recipe.prep_time_min} min</span> : null}
          {recipe.cook_time_min ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon type="clock" size={12} />Cuisson {recipe.cook_time_min} min</span> : null}
          {recipe.servings ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon type="user" size={12} />{recipe.servings} pers.</span> : null}
        </div>
      )}
      {ingredients.length > 0 && (
        <div style={{ marginBottom: preparation.length > 0 ? 8 : 0 }}>
          <div style={{ fontSize: '.7rem', fontWeight: 700, color: '#e5b878', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Ingrédients</div>
          <ul style={{ margin: 0, paddingLeft: 16, color: '#d4c9a8' }}>
            {ingredients.map((it, i) => (
              <li key={i} style={{ marginBottom: 2 }}>{it}</li>
            ))}
          </ul>
        </div>
      )}
      {preparation.length > 0 && (
        <div>
          <div style={{ fontSize: '.7rem', fontWeight: 700, color: '#e5b878', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Préparation</div>
          <ol style={{ margin: 0, paddingLeft: 18, color: '#d4c9a8' }}>
            {preparation.map((step, i) => (
              <li key={i} style={{ marginBottom: 3, paddingLeft: 4 }}>{step}</li>
            ))}
          </ol>
        </div>
      )}
      {recipe.tip && (
        <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(106, 191, 138, 0.06)', borderLeft: '2px solid rgba(106, 191, 138, 0.4)', borderRadius: 4, fontSize: '.78rem', color: '#8abf9a', fontStyle: 'italic' }}>
          <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}><Icon type="bulb" size={13} style={{ marginTop: 2 }} />{recipe.tip}</span>
        </div>
      )}
    </div>
  );
}

// ─── V97.13.38 — Carte Lettre d'intro éditable inline ─────────────────────
//
// Variante de CapsCard pour la lettre d'intro avec edition manuelle du
// greeting et de la signature (Phase A1 option C). Permet a Anissa de
// peaufiner ces 2 champs courts sans quitter la modal. La persistance se
// fait via consultation.intro_letter (deja gere par le mapper en priorite).
// Les paragraphes body et les tailored_points restent geres par l'IA
// Enrichir (Phase B option C, deja en place).

function useDirtyReporter(section, dirty, onDirtyChange) {
  useEffect(() => {
    if (typeof onDirtyChange !== 'function') return undefined;
    onDirtyChange(section, dirty);
    return () => onDirtyChange(section, false);
  }, [section, dirty, onDirtyChange]);
}

function IntroCardEditable({ intro, onImprove, improving = false, onSave, onDirtyChange, saving = false, savedAt = null, error = null }) {
  const empty = !intro?.body?.length;
  const [editing, setEditing] = useState(false);
  const [greeting, setGreeting] = useState(intro?.greeting || '');
  const [signature, setSignature] = useState(intro?.signature || 'Anissa');
  // A2 V97.13.39 — edition body paragraphes + pull_quote
  const [bodyParas, setBodyParas] = useState(intro?.body || []);
  const [pullQuote, setPullQuote] = useState(intro?.pull_quote || '');

  // Sync local state quand intro change (apres save reussi par exemple)
  useEffect(() => {
    if (!editing) {
      setGreeting(intro?.greeting || '');
      setSignature(intro?.signature || 'Anissa');
      setBodyParas(intro?.body || []);
      setPullQuote(intro?.pull_quote || '');
    }
  }, [intro?.greeting, intro?.signature, intro?.body, intro?.pull_quote, editing]);

  const canSave = typeof onSave === 'function' && !empty;
  const initialBody = (intro?.body || []).join('\n\n');
  const currentBody = bodyParas.join('\n\n');
  const dirty = (greeting.trim() !== (intro?.greeting || '').trim())
             || (signature.trim() !== (intro?.signature || 'Anissa').trim())
             || (currentBody !== initialBody)
             || (pullQuote.trim() !== (intro?.pull_quote || '').trim());
  useDirtyReporter('intro', dirty, onDirtyChange);

  const handleConfirm = async () => {
    if (!onSave) return;
    await onSave({
      greeting: greeting.trim(),
      signature: signature.trim(),
      body: bodyParas.map((p) => p.trim()).filter(Boolean),
      pull_quote: pullQuote.trim(),
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setGreeting(intro?.greeting || '');
    setSignature(intro?.signature || 'Anissa');
    setBodyParas(intro?.body || []);
    setPullQuote(intro?.pull_quote || '');
    setEditing(false);
  };

  const updateParagraph = (idx, value) => {
    setBodyParas((arr) => arr.map((p, i) => (i === idx ? value : p)));
  };
  const insertParagraphAfter = (idx) => {
    setBodyParas((arr) => {
      const next = [...arr];
      next.splice(idx + 1, 0, '');
      return next;
    });
  };
  const removeParagraph = (idx) => {
    setBodyParas((arr) => arr.filter((_, i) => i !== idx));
  };

  return (
    <div style={capsCardStyle}>
      <header style={capsCardHeaderStyle}>
        <Icon type="mail" size={18} style={{ color: '#d4c9a8' }} />
        <div style={{ flex: 1 }}>
          <h4 style={capsCardTitleStyle}>Lettre d'intro</h4>
          {savedAt && (
            <p style={{ ...capsCardSubtitleStyle, color: '#8abf9a', display: 'flex', alignItems: 'center', gap: 5 }}><Icon type="check" size={11} />Enregistré</p>
          )}
        </div>
        {empty && <span style={capsCardBadgeStyle}>à enrichir</span>}
        {!empty && canSave && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{
              background: 'rgba(212, 201, 168, 0.08)',
              border: '1px solid rgba(212, 201, 168, 0.20)',
              color: '#d4c9a8',
              padding: '5px 11px',
              borderRadius: 6,
              fontSize: '.75rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            title="Modifier le bonjour et la signature"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="pencil" size={12} />Éditer</span>
          </button>
        )}
        {!editing && onImprove && (
          <button
            type="button"
            onClick={onImprove}
            disabled={improving}
            style={{
              background: 'rgba(120, 80, 200, 0.08)',
              border: '1px solid rgba(180, 140, 255, 0.25)',
              color: '#b89eff',
              padding: '5px 11px',
              borderRadius: 6,
              fontSize: '.75rem',
              fontWeight: 600,
              cursor: improving ? 'wait' : 'pointer',
              opacity: improving ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
            title="Préparer une nouvelle proposition de lettre"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="pencil" size={12} />{improving ? 'Préparation…' : 'Améliorer'}</span>
          </button>
        )}
      </header>

      <div style={capsCardBodyStyle}>
        {empty ? (
          <p style={capsEmptyStyle}>Pas encore de lettre d'intro générée</p>
        ) : editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 600, color: '#8a8a7a', letterSpacing: '.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Bonjour
              </label>
              <input
                type="text"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.18)',
                  border: '1px solid rgba(212, 201, 168, 0.20)',
                  borderRadius: 6,
                  color: '#f0f0e8',
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: '1.05rem',
                }}
                placeholder="Bonjour Camille,"
              />
            </div>

            {/* A2 V97.13.39 — Body paragraphes editables (1 textarea par para) */}
            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 600, color: '#8a8a7a', letterSpacing: '.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Corps de la lettre
              </label>
              {bodyParas.length === 0 && (
                <p style={{ fontSize: '.8rem', color: '#666', fontStyle: 'italic', margin: '0 0 8px' }}>
                  Aucun paragraphe. Utilise « Améliorer » pour préparer une proposition, puis relis-la ici.
                </p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {bodyParas.map((para, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <textarea
                      value={para}
                      onChange={(e) => updateParagraph(i, e.target.value)}
                      rows={Math.max(3, Math.min(8, (para.match(/\n/g)?.length || 0) + 3))}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        background: 'rgba(0,0,0,0.18)',
                        border: '1px solid rgba(212, 201, 168, 0.20)',
                        borderRadius: 6,
                        color: '#f0f0e8',
                        fontFamily: 'inherit',
                        fontSize: '.92rem',
                        lineHeight: 1.55,
                        resize: 'vertical',
                      }}
                      placeholder={`Paragraphe ${i + 1}…`}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button
                        type="button"
                        onClick={() => insertParagraphAfter(i)}
                        title="Ajouter un paragraphe après celui-ci"
                        style={{
                          background: 'rgba(212, 201, 168, 0.06)',
                          border: '1px solid rgba(212, 201, 168, 0.18)',
                          color: '#8a8a7a',
                          borderRadius: 4,
                          width: 28,
                          height: 28,
                          cursor: 'pointer',
                          fontSize: '.85rem',
                        }}
                      >＋</button>
                      <button
                        type="button"
                        onClick={() => removeParagraph(i)}
                        title="Supprimer ce paragraphe"
                        style={{
                          background: 'rgba(220, 80, 80, 0.06)',
                          border: '1px solid rgba(220, 80, 80, 0.18)',
                          color: '#d4806c',
                          borderRadius: 4,
                          width: 28,
                          height: 28,
                          cursor: 'pointer',
                          fontSize: '.75rem',
                        }}
                      ><Icon type="trash" size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
              {bodyParas.length === 0 && (
                <button
                  type="button"
                  onClick={() => insertParagraphAfter(-1)}
                  style={{
                    marginTop: 8,
                    background: 'rgba(212, 201, 168, 0.06)',
                    border: '1px dashed rgba(212, 201, 168, 0.25)',
                    color: '#8a8a7a',
                    padding: '8px 12px',
                    borderRadius: 6,
                    fontSize: '.8rem',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >＋ Ajouter un paragraphe</button>
              )}
            </div>

            {/* A2 V97.13.39 — Pull quote optionnel */}
            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 600, color: '#8a8a7a', letterSpacing: '.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Citation marquante (optionnel)
              </label>
              <textarea
                value={pullQuote}
                onChange={(e) => setPullQuote(e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.18)',
                  border: '1px solid rgba(212, 201, 168, 0.20)',
                  borderRadius: 6,
                  color: '#f0f0e8',
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: '1.0rem',
                  lineHeight: 1.5,
                  resize: 'vertical',
                }}
                placeholder="Ex: La régularité reconstruit ce qui demande du temps."
              />
              <p style={{ fontSize: '.72rem', color: '#666', margin: '4px 0 0' }}>
                Affichée en bloc italique vert dans l'app de la cliente. Laisse vide pour ne pas la montrer.
              </p>
            </div>

            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 600, color: '#8a8a7a', letterSpacing: '.08em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
                Signature
              </label>
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.18)',
                  border: '1px solid rgba(212, 201, 168, 0.20)',
                  borderRadius: 6,
                  color: '#f0f0e8',
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: '1.0rem',
                }}
                placeholder="Anissa"
              />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 4 }}>
              {error && (
                <span style={{ fontSize: '.75rem', color: '#f5c6c6', marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}><Icon type="warning" size={12} />{error}</span>
              )}
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#8a8a7a',
                  padding: '8px 14px',
                  borderRadius: 6,
                  fontSize: '.82rem',
                  cursor: saving ? 'wait' : 'pointer',
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving || !dirty}
                style={{
                  background: dirty ? 'rgba(106, 191, 138, 0.15)' : 'rgba(106, 191, 138, 0.06)',
                  border: `1px solid ${dirty ? 'rgba(106, 191, 138, 0.35)' : 'rgba(106, 191, 138, 0.18)'}`,
                  color: dirty ? '#a8e0c0' : '#666',
                  padding: '8px 16px',
                  borderRadius: 6,
                  fontSize: '.82rem',
                  fontWeight: 600,
                  cursor: (saving || !dirty) ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {intro?.greeting && (
              <p style={capsGreetingStyle}>{intro.greeting}</p>
            )}
            {intro?.body?.map((para, i) => (
              <p key={i} style={capsBodyStyle}>{para}</p>
            ))}
            {intro?.pull_quote && (
              <blockquote style={capsQuoteStyle}>« {intro.pull_quote} »</blockquote>
            )}
            {intro?.tailored_points?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {intro.tailored_points.map((tp, i) => (
                  <div key={tp.id || i} style={{ marginBottom: 10 }}>
                    <div style={capsPointTitleStyle}>{tp.title}</div>
                    <div style={capsPointDetailStyle}>{tp.detail}</div>
                  </div>
                ))}
              </div>
            )}
            {intro?.signature && (
              <p style={capsSignatureStyle}>— {intro.signature}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── V97.13.41 Phase A3a — Carte Stratégie éditable inline ──────────────
//
// Variante de CapsCard pour la stratégie avec édition manuelle des 3 piliers
// (Axe principal / Structure alimentaire / Priorités d'action typiquement).
// Persistance via consultation.editorial_overrides.strategy (champ JSONB
// generique nouveau pour les overrides editoriaux par section).
// Reste les boutons Améliorer + Éditer comme sur l'intro.

function StrategyCardEditable({ strategy, onImprove, improving = false, onSave, onDirtyChange, saving = false, savedAt = null, error = null }) {
  const empty = !strategy?.pillars?.length;
  const [editing, setEditing] = useState(false);
  const [pillars, setPillars] = useState(strategy?.pillars || []);

  useEffect(() => {
    if (!editing) {
      setPillars(strategy?.pillars || []);
    }
  }, [strategy?.pillars, editing]);

  const canSave = typeof onSave === 'function' && !empty;
  const initial = JSON.stringify((strategy?.pillars || []).map((p) => ({ title: p.title || '', description: p.description || '' })));
  const current = JSON.stringify(pillars.map((p) => ({ title: p.title || '', description: p.description || '' })));
  const dirty = current !== initial;
  useDirtyReporter('strategy', dirty, onDirtyChange);

  const handleConfirm = async () => {
    if (!onSave) return;
    await onSave({
      pillars: pillars
        .map((p) => ({ title: (p.title || '').trim(), description: (p.description || '').trim() }))
        .filter((p) => p.title && p.description),
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setPillars(strategy?.pillars || []);
    setEditing(false);
  };

  const updatePillar = (idx, field, value) => {
    setPillars((arr) => arr.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  return (
    <div style={capsCardStyle}>
      <header style={capsCardHeaderStyle}>
        <Icon type="tasks" size={18} style={{ color: '#d4c9a8' }} />
        <div style={{ flex: 1 }}>
          <h4 style={capsCardTitleStyle}>Stratégie</h4>
          {savedAt
            ? <p style={{ ...capsCardSubtitleStyle, color: '#8abf9a', display: 'flex', alignItems: 'center', gap: 5 }}><Icon type="check" size={11} />Enregistré</p>
            : (strategy?.subtitle && <p style={capsCardSubtitleStyle}>{strategy.subtitle}</p>)}
        </div>
        {empty && <span style={capsCardBadgeStyle}>à enrichir</span>}
        {!empty && canSave && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{
              background: 'rgba(212, 201, 168, 0.08)',
              border: '1px solid rgba(212, 201, 168, 0.20)',
              color: '#d4c9a8',
              padding: '5px 11px',
              borderRadius: 6,
              fontSize: '.75rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            title="Modifier les piliers stratégiques"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="pencil" size={12} />Éditer</span>
          </button>
        )}
        {!editing && onImprove && (
          <button
            type="button"
            onClick={onImprove}
            disabled={improving}
            style={{
              background: 'rgba(120, 80, 200, 0.08)',
              border: '1px solid rgba(180, 140, 255, 0.25)',
              color: '#b89eff',
              padding: '5px 11px',
              borderRadius: 6,
              fontSize: '.75rem',
              fontWeight: 600,
              cursor: improving ? 'wait' : 'pointer',
              opacity: improving ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
            title="Préparer une nouvelle proposition de stratégie"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="pencil" size={12} />{improving ? 'Préparation…' : 'Améliorer'}</span>
          </button>
        )}
      </header>

      <div style={capsCardBodyStyle}>
        {empty ? (
          <p style={capsEmptyStyle}>Pas de piliers détectés dans la stratégie</p>
        ) : editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {pillars.map((p, i) => (
              <div key={i} style={{
                background: 'rgba(0,0,0,0.12)',
                border: '1px solid rgba(212, 201, 168, 0.12)',
                borderRadius: 8,
                padding: 12,
              }}>
                <label style={{ fontSize: '.7rem', fontWeight: 600, color: '#8a8a7a', letterSpacing: '.08em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                  Pilier {i + 1} — Titre
                </label>
                <input
                  type="text"
                  value={p.title || ''}
                  onChange={(e) => updatePillar(i, 'title', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    background: 'rgba(0,0,0,0.18)',
                    border: '1px solid rgba(212, 201, 168, 0.18)',
                    borderRadius: 6,
                    color: '#f0f0e8',
                    fontFamily: 'inherit',
                    fontSize: '.9rem',
                    fontWeight: 600,
                    marginBottom: 8,
                  }}
                  placeholder="Axe principal"
                />
                <label style={{ fontSize: '.7rem', fontWeight: 600, color: '#8a8a7a', letterSpacing: '.08em', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                  Description
                </label>
                <textarea
                  value={p.description || ''}
                  onChange={(e) => updatePillar(i, 'description', e.target.value)}
                  rows={Math.max(2, Math.min(5, ((p.description || '').length / 80) + 1))}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    background: 'rgba(0,0,0,0.18)',
                    border: '1px solid rgba(212, 201, 168, 0.18)',
                    borderRadius: 6,
                    color: '#cfcfc4',
                    fontFamily: 'inherit',
                    fontSize: '.85rem',
                    lineHeight: 1.5,
                    resize: 'vertical',
                  }}
                  placeholder="Description du pilier…"
                />
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 4 }}>
              {error && <span style={{ fontSize: '.75rem', color: '#f5c6c6', marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}><Icon type="warning" size={12} />{error}</span>}
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#8a8a7a',
                  padding: '8px 14px',
                  borderRadius: 6,
                  fontSize: '.82rem',
                  cursor: saving ? 'wait' : 'pointer',
                }}
              >Annuler</button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving || !dirty}
                style={{
                  background: dirty ? 'rgba(106, 191, 138, 0.15)' : 'rgba(106, 191, 138, 0.06)',
                  border: `1px solid ${dirty ? 'rgba(106, 191, 138, 0.35)' : 'rgba(106, 191, 138, 0.18)'}`,
                  color: dirty ? '#a8e0c0' : '#666',
                  padding: '8px 16px',
                  borderRadius: 6,
                  fontSize: '.82rem',
                  fontWeight: 600,
                  cursor: (saving || !dirty) ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
            </div>
          </div>
        ) : (
          <>
            {strategy?.essential?.length > 0 && strategy.essential.map((p, i) => (
              <p key={i} style={capsBodyStyle}>{p}</p>
            ))}
            <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
              {strategy.pillars.map((p) => (
                <div key={p.id} style={capsPillarStyle}>
                  <div style={capsPillarTitleStyle}>{p.title}</div>
                  <div style={capsPillarDescStyle}>{p.description}</div>
                </div>
              ))}
            </div>
            {strategy?.takeaways?.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={capsSubTitleStyle}>{strategy.takeaways_title || 'À retenir'}</div>
                <ul style={capsListStyle}>
                  {strategy.takeaways.map((t, i) => (
                    <li key={i} style={capsListItemStyle}>{t}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Carte Compléments éditable pour l'app cliente ──────────────────────
//
// L'intro reste éditable. Anissa peut aussi masquer/restaurer une carte
// complète dans l'app cliente. La source et le Word ne sont jamais modifiés :
// seuls les IDs exclus sont persistés dans editorial_overrides.supplements.

function SupplementsCardEditable({ protocols, sourceProtocols, supplementOverride, onImprove, improving = false, onSave, onDirtyChange, saving = false, savedAt = null, error = null }) {
  const allProtocols = sourceProtocols || protocols;
  const empty = !allProtocols?.groups?.length;
  const currentIntro = protocols?.intro || allProtocols?.intro || '';
  const parsedOverride = parseSupplementEditorialOverride(supplementOverride);
  const currentExcluded = parsedOverride?.excluded_item_ids || [];
  const [editing, setEditing] = useState(false);
  const [intro, setIntro] = useState(currentIntro);
  const [excludedIds, setExcludedIds] = useState(currentExcluded);

  useEffect(() => {
    if (!editing) {
      setIntro(currentIntro);
      setExcludedIds(currentExcluded);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIntro, JSON.stringify(currentExcluded), editing]);

  const canSave = typeof onSave === 'function' && !empty;
  const normalizeIds = (ids) => [...ids].sort().join('|');
  const dirty = (intro || '').trim() !== (currentIntro || '').trim()
    || normalizeIds(excludedIds) !== normalizeIds(currentExcluded);
  useDirtyReporter('supplements', dirty, onDirtyChange);

  const handleConfirm = async () => {
    if (!onSave) return;
    try {
      await onSave({ intro: intro.trim(), excluded_item_ids: excludedIds });
      setEditing(false);
    } catch {
      // Le parent affiche l'erreur persistée. L'éditeur reste ouvert et le
      // brouillon local n'est jamais présenté comme enregistré.
    }
  };

  const handleCancel = () => {
    setIntro(currentIntro);
    setExcludedIds(currentExcluded);
    setEditing(false);
  };

  const toggleExcluded = (itemId) => {
    setExcludedIds((current) => (
      current.includes(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId]
    ));
  };

  const displayGroups = editing ? (allProtocols?.groups || []) : (protocols?.groups || []);

  return (
    <div style={capsCardStyle}>
      <header style={capsCardHeaderStyle}>
        <Icon type="package" size={18} style={{ color: '#d4c9a8' }} />
        <div style={{ flex: 1 }}>
          <h4 style={capsCardTitleStyle}>Compléments</h4>
          {savedAt
            ? <p style={{ ...capsCardSubtitleStyle, color: '#8abf9a', display: 'flex', alignItems: 'center', gap: 5 }}><Icon type="check" size={11} />Enregistré</p>
            : (protocols?.header_title && <p style={capsCardSubtitleStyle}>{protocols.header_title}</p>)}
        </div>
        {empty && <span style={capsCardBadgeStyle}>à enrichir</span>}
        {!empty && canSave && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{
              background: 'rgba(212, 201, 168, 0.08)',
              border: '1px solid rgba(212, 201, 168, 0.20)',
              color: '#d4c9a8',
              padding: '5px 11px',
              borderRadius: 6,
              fontSize: '.75rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            title="Choisir les compléments visibles dans l'app cliente"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="pencil" size={12} />Modifier pour l’app</span>
          </button>
        )}
        {!editing && onImprove && (
          <button
            type="button"
            onClick={onImprove}
            disabled={improving}
            style={{
              background: 'rgba(120, 80, 200, 0.08)',
              border: '1px solid rgba(180, 140, 255, 0.25)',
              color: '#b89eff',
              padding: '5px 11px',
              borderRadius: 6,
              fontSize: '.75rem',
              fontWeight: 600,
              cursor: improving ? 'wait' : 'pointer',
              opacity: improving ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
            title="Préparer une nouvelle proposition d’introduction"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="pencil" size={12} />{improving ? 'Préparation…' : 'Améliorer l’introduction'}</span>
          </button>
        )}
      </header>

      <div style={capsCardBodyStyle}>
        {empty ? (
          <p style={capsEmptyStyle}>Pas de compléments détectés</p>
        ) : (
          <>
            {/* Intro narrative — édition manuelle ou affichage */}
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
                <label style={{ fontSize: '.7rem', fontWeight: 600, color: '#8a8a7a', letterSpacing: '.08em', textTransform: 'uppercase' }}>
                  Intro narrative
                </label>
                <textarea
                  value={intro}
                  onChange={(e) => setIntro(e.target.value)}
                  rows={Math.max(2, Math.min(6, ((intro || '').length / 80) + 2))}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0,0,0,0.18)',
                    border: '1px solid rgba(212, 201, 168, 0.18)',
                    borderRadius: 6,
                    color: '#cfcfc4',
                    fontFamily: 'inherit',
                    fontSize: '.9rem',
                    lineHeight: 1.55,
                    resize: 'vertical',
                  }}
                  placeholder="Ex: Camille, voici les compléments qui vont soutenir ton parcours..."
                />
                <p style={{ fontSize: '.72rem', color: '#666', margin: 0 }}>
                  Les suppressions ci-dessous concernent uniquement l’app cliente. Le document Word et la source ne changent pas.
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 4 }}>
                  {error && <span style={{ fontSize: '.75rem', color: '#f5c6c6', marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}><Icon type="warning" size={12} />{error}</span>}
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={saving}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: '#8a8a7a',
                      padding: '8px 14px',
                      borderRadius: 6,
                      fontSize: '.82rem',
                      cursor: saving ? 'wait' : 'pointer',
                    }}
                  >Annuler</button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={saving || !dirty}
                    style={{
                      background: dirty ? 'rgba(106, 191, 138, 0.15)' : 'rgba(106, 191, 138, 0.06)',
                      border: `1px solid ${dirty ? 'rgba(106, 191, 138, 0.35)' : 'rgba(106, 191, 138, 0.18)'}`,
                      color: dirty ? '#a8e0c0' : '#666',
                      padding: '8px 16px',
                      borderRadius: 6,
                      fontSize: '.82rem',
                      fontWeight: 600,
                      cursor: (saving || !dirty) ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.6 : 1,
                    }}
                  >{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
                </div>
              </div>
            ) : currentIntro ? (
              <p style={{ ...capsBodyStyle, fontStyle: 'italic', color: '#d4c9a8', marginBottom: 14 }}>
                {currentIntro}
              </p>
            ) : null}

            {!editing && displayGroups.length === 0 && (
              <p style={capsEmptyStyle}>Tous les compléments sont masqués dans l’app cliente.</p>
            )}

            {/* En édition, la source complète reste visible pour permettre
                suppression et restauration sans toucher au plan/Word. */}
            {displayGroups.map((g) => (
              <div key={g.id} style={{ marginBottom: 12 }}>
                <div style={capsSubTitleStyle}>{g.title}</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {g.items?.map((it) => {
                    const excluded = excludedIds.includes(it.id);
                    return (
                    <div key={it.id} style={{ ...capsCompStyle, opacity: excluded ? .55 : 1 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ ...capsCompNameStyle, flex: 1 }}>{it.name}</div>
                        {editing && (
                          <button
                            type="button"
                            onClick={() => toggleExcluded(it.id)}
                            style={{
                              background: excluded ? 'rgba(138,191,154,.10)' : 'rgba(217,166,166,.08)',
                              border: `1px solid ${excluded ? 'rgba(138,191,154,.3)' : 'rgba(217,166,166,.25)'}`,
                              color: excluded ? '#a8e0c0' : '#d9a6a6',
                              borderRadius: 6,
                              padding: '5px 9px',
                              fontSize: '.72rem',
                              cursor: 'pointer',
                            }}
                          >{excluded ? '↶ Restaurer' : '× Retirer de l’app'}</button>
                        )}
                      </div>
                      {it.dose && (
                        <div style={capsCompMetaStyle}>
                          <Icon type="package" size={13} style={{ marginTop: 1 }} />
                          <span>{it.dose}</span>
                        </div>
                      )}
                      {it.timing_detail && (
                        <div style={capsCompMetaStyle}>
                          <Icon type="clock" size={13} style={{ marginTop: 1 }} />
                          <span>{it.timing_detail}</span>
                        </div>
                      )}
                      {it.benefit && <div style={capsCompBenefitStyle}>{it.benefit}</div>}
                      {excluded && editing && (
                        <div style={{ marginTop: 6, color: '#d9a6a6', fontSize: '.7rem' }}>Masqué de l’app après enregistrement</div>
                      )}
                    </div>
                  );})}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// ─── V97.13.43 Phase A3c — Carte Frigo & courses éditable ──────────────
//
// Listes "À privilégier" et "À limiter" éditables comme tags/pills.
// Anissa peut ajouter, supprimer ou renommer chaque item. Persistance via
// consultation.editorial_overrides.fridge = { to_favor[], to_limit[] }.
// Le détail rapproche les repères frigo, le Meal Prep publié et ses courses.

function countFridgeReviewItems(fridge) {
  const essentials = Array.isArray(fridge?.essentials) ? fridge.essentials.length : 0;
  return essentials + ['favorite', 'limit'].reduce((total, key) => total
    + (Array.isArray(fridge?.[key])
      ? fridge[key].reduce((count, category) => count
        + (Array.isArray(category?.items) ? category.items.length : 0), 0)
      : 0), 0);
}

function buildMealPrepReviewWeeks(prep) {
  if (!prep || !Array.isArray(prep.sessions)) return [];
  const sourceWeeks = prep.version === 2 && Array.isArray(prep.rotation?.weeks)
    ? prep.rotation.weeks
    : [{ id: 'published-week', label: 'Semaine publiée', sessions: prep.sessions, shopping_list: prep.shopping_list }];
  return sourceWeeks.map((week, index) => ({
    id: String(week?.id || `week-${index + 1}`),
    label: String(week?.label || `Semaine ${index + 1}`),
    sessions: Array.isArray(week?.sessions) ? week.sessions : [],
    shoppingList: Array.isArray(week?.shopping_list) ? week.shopping_list : [],
  }));
}

function mealPrepShoppingLabel(week) {
  const count = week.shoppingList.length;
  if (count > 0) return `${count} article${count > 1 ? 's' : ''}`;
  return 'liste de courses non structurée';
}

function formatMealPrepQuantity(item) {
  const quantity = item?.quantity;
  const unit = String(item?.unit || '').trim();
  if (quantity === undefined || quantity === null || quantity === '') return '';
  return `${quantity}${unit ? ` ${unit}` : ''}`;
}

function FridgeMealPrepDetails({ prep }) {
  const weeks = buildMealPrepReviewWeeks(prep);
  if (weeks.length === 0) return null;
  const isV2 = prep?.version === 2;

  return (
    <section className="delivery-fridge-detail" aria-label="Contenu Meal Prep et liste de courses">
      <header className="delivery-fridge-detail__header">
        <div>
          <p>Contenu lié</p>
          <h5>{isV2 ? 'Meal Prep et courses du cycle' : 'Meal Prep de la semaine'}</h5>
        </div>
        <span>{weeks.length} semaine{weeks.length > 1 ? 's' : ''}</span>
      </header>
      <div className="delivery-fridge-detail__weeks">
        {weeks.map((week) => (
          <details key={week.id} className="delivery-fridge-detail__week" open>
            <summary>
              <span>{week.label}</span>
              <small>{week.sessions.length} session{week.sessions.length > 1 ? 's' : ''} · {mealPrepShoppingLabel(week)}</small>
            </summary>
            <div className="delivery-fridge-detail__week-body">
              {week.sessions.map((session, sessionIndex) => {
                const tasks = Array.isArray(session?.tasks) ? session.tasks : [];
                const components = Array.isArray(session?.components) ? session.components : [];
                return (
                  <article key={session?.id || `${week.id}-session-${sessionIndex + 1}`} className="delivery-fridge-detail__session">
                    <div className="delivery-fridge-detail__session-title">
                      <strong>{session?.title || `Session ${sessionIndex + 1}`}</strong>
                      {(session?.preferred_day || session?.estimated_minutes) && (
                        <small>{[session.preferred_day, session.estimated_minutes ? `${session.estimated_minutes} min` : ''].filter(Boolean).join(' · ')}</small>
                      )}
                    </div>
                    {tasks.length > 0 && (
                      <ul>
                        {tasks.map((task, taskIndex) => (
                          <li key={task?.id || `${week.id}-task-${taskIndex + 1}`}>
                            <strong>{task?.label || 'Préparation'}</strong>
                            {task?.detail && <span>{task.detail}</span>}
                            {Array.isArray(task?.serves_days) && task.serves_days.length > 0 && <small>Prévu pour : {task.serves_days.join(', ')}</small>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {components.length > 0 && (
                      <ul>
                        {components.map((component, componentIndex) => (
                          <li key={component?.id || `${week.id}-component-${componentIndex + 1}`}>
                            <strong>{component?.label || 'Préparation'}{formatMealPrepQuantity(component) ? ` — ${formatMealPrepQuantity(component)}` : ''}</strong>
                            {component?.preparation && <span>{component.preparation}</span>}
                            {Array.isArray(component?.usages) && component.usages.length > 0 && (
                              <small>Cela servira pour : {component.usages.map((usage) => [usage?.day, usage?.meal_title].filter(Boolean).join(' — ')).filter(Boolean).join(', ')}</small>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                );
              })}
              {week.shoppingList.length > 0 && (
                <div className="delivery-fridge-detail__shopping">
                  <h6>Liste de courses</h6>
                  <ul>
                    {week.shoppingList.map((item, itemIndex) => (
                      <li key={item?.food_key || `${week.id}-shopping-${itemIndex + 1}`}>
                        <span>{item?.label || 'Article'}</span>
                        <strong>{formatMealPrepQuantity(item) || 'quantité selon les recettes'}</strong>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

function FridgeCardEditable({ fridge, weekMeals, mealPrepValue, fridgeSyncValue, onSaveFridgeSync, onImprove, improving = false, onSave, onDirtyChange, saving = false, savedAt = null, error = null, compactReview = false }) {
  const empty = !fridge?.essentials?.length && !fridge?.favorite?.length && !fridge?.limit?.length;
  const essentialsLabels = (fridge?.essentials || []).map((e) => e.label || '').filter(Boolean);
  const limitLabels = (fridge?.limit || []).flatMap((cat) => (cat.items || []).map((it) => it.label || '')).filter(Boolean);
  const [editing, setEditing] = useState(false);
  const [toFavor, setToFavor] = useState(essentialsLabels);
  const [toLimit, setToLimit] = useState(limitLabels);
  const [newFavor, setNewFavor] = useState('');
  const [newLimit, setNewLimit] = useState('');
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncSaving, setSyncSaving] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(!compactReview);
  const usesMealPrepV2 = weekMeals?.prep?.version === 2;
  const syncIssue = usesMealPrepV2 ? null : getFridgeSyncPublicationIssue(
    weekMeals,
    mealPrepValue,
    fridge,
    fridgeSyncValue,
  );
  const syncRequired = !usesMealPrepV2
    && Boolean(buildFridgeSyncSourceRevision(weekMeals, mealPrepValue, fridge));
  const fridgeRepereCount = countFridgeReviewItems(fridge);
  const mealPrepReviewWeeks = buildMealPrepReviewWeeks(weekMeals?.prep);
  const shoppingArticleCount = mealPrepReviewWeeks.length > 0
    ? mealPrepReviewWeeks.reduce((total, week) => total + week.shoppingList.length, 0)
    : null;
  const shoppingArticleLabel = mealPrepReviewWeeks.length > 1 ? 'articles sur le cycle' : 'articles de courses';
  const fridgeStatusLabel = empty ? 'Manquant' : (syncIssue ? 'À vérifier' : 'Complet');
  const syncButtonStyle = {
    borderRadius: 7,
    border: '1px solid rgba(212, 201, 168, 0.18)',
    background: 'transparent',
    color: '#cfcfc4',
    fontFamily: 'inherit',
    fontSize: '.74rem',
    padding: '7px 10px',
    cursor: 'pointer',
  };

  useEffect(() => {
    if (!editing) {
      setToFavor(essentialsLabels);
      setToLimit(limitLabels);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(essentialsLabels), JSON.stringify(limitLabels), editing]);

  const canSave = typeof onSave === 'function' && !empty;
  const dirty = JSON.stringify(toFavor) !== JSON.stringify(essentialsLabels)
             || JSON.stringify(toLimit) !== JSON.stringify(limitLabels);
  useDirtyReporter('fridge', dirty, onDirtyChange);
  useDirtyReporter('fridge-sync', Boolean(syncPreview), onDirtyChange);

  useEffect(() => {
    setSyncPreview(null);
    setSyncError(null);
  }, [fridgeSyncValue]);

  const handleConfirm = async () => {
    if (!onSave) return;
    await onSave({ to_favor: toFavor, to_limit: toLimit });
    setEditing(false);
  };

  const handleCancel = () => {
    setToFavor(essentialsLabels);
    setToLimit(limitLabels);
    setEditing(false);
  };

  const removeAt = (list, setList, idx) => setList(list.filter((_, i) => i !== idx));
  const addItem = (list, setList, value, setValue) => {
    const v = (value || '').trim();
    if (!v) return;
    if (list.some((x) => x.toLowerCase() === v.toLowerCase())) {
      setValue('');
      return;
    }
    setList([...list, v]);
    setValue('');
  };

  const prepareSyncPreview = () => {
    try {
      setSyncPreview(buildFridgeSyncPreview(weekMeals, mealPrepValue, fridge));
      setSyncError(null);
    } catch (syncErr) {
      setSyncPreview(null);
      setSyncError(syncErr?.message || 'La fiche frigo ne peut pas encore être actualisée.');
    }
  };

  const confirmSync = async () => {
    if (!syncPreview || typeof onSaveFridgeSync !== 'function') return;
    setSyncSaving(true);
    setSyncError(null);
    try {
      await onSaveFridgeSync(syncPreview.override);
      setSyncPreview(null);
    } catch (syncErr) {
      setSyncError(syncErr?.message || 'Actualisation impossible.');
    } finally {
      setSyncSaving(false);
    }
  };

  const renderPills = (list, setList, color) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {list.map((label, i) => (
        <span key={i} style={{
          ...capsPillStyle,
          ...(color === 'limit' ? capsPillLimitStyle : {}),
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 4px 4px 10px',
        }}>
          {label}
          <button
            type="button"
            onClick={() => removeAt(list, setList, i)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              opacity: 0.6,
              padding: '0 4px',
              fontSize: '.75rem',
            }}
            title="Supprimer"
          >×</button>
        </span>
      ))}
    </div>
  );

  return (
    <div style={capsCardStyle}>
      <header style={capsCardHeaderStyle}>
        <Icon type="calendar" size={18} style={{ color: '#d4c9a8' }} />
        <div style={{ flex: 1 }}>
          <h4 style={capsCardTitleStyle}>Frigo & courses</h4>
          {savedAt
            ? <p style={{ ...capsCardSubtitleStyle, color: '#8abf9a', display: 'flex', alignItems: 'center', gap: 5 }}><Icon type="check" size={11} />Enregistré</p>
            : (fridge?.header_title && <p style={capsCardSubtitleStyle}>{fridge.header_title}</p>)}
        </div>
        {empty && <span style={capsCardBadgeStyle}>à enrichir</span>}
        {!empty && canSave && !editing && (
          <button
            type="button"
            onClick={() => { setDetailsOpen(true); setEditing(true); }}
            style={{
              background: 'rgba(212, 201, 168, 0.08)',
              border: '1px solid rgba(212, 201, 168, 0.20)',
              color: '#d4c9a8',
              padding: '5px 11px',
              borderRadius: 6,
              fontSize: '.75rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            title="Modifier les listes à privilégier et à limiter"
          ><span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="pencil" size={12} />Éditer</span></button>
        )}
        {!editing && onImprove && (
          <button
            type="button"
            onClick={onImprove}
            disabled={improving}
            style={{
              background: 'rgba(120, 80, 200, 0.08)',
              border: '1px solid rgba(180, 140, 255, 0.25)',
              color: '#b89eff',
              padding: '5px 11px',
              borderRadius: 6,
              fontSize: '.75rem',
              fontWeight: 600,
              cursor: improving ? 'wait' : 'pointer',
              opacity: improving ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
            title="Préparer une nouvelle proposition pour le frigo"
          ><span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon type="pencil" size={12} />{improving ? 'Préparation…' : 'Améliorer'}</span></button>
        )}
      </header>

      {compactReview && !editing && (
        <section className="delivery-quick-review" aria-label="Résumé du frigo et des courses">
          <div className="delivery-quick-review__heading">
            <div>
              <p>État de la section</p>
              <h5>Frigo & courses — {fridgeStatusLabel}</h5>
            </div>
            <span className={`delivery-quick-review__state${fridgeStatusLabel === 'Complet' ? ' is-ready' : ''}`}>
              {fridgeStatusLabel}
            </span>
          </div>
          <dl className="delivery-quick-review__metrics">
            <div><dt>{fridgeRepereCount}</dt><dd>repères frigo</dd></div>
            <div><dt>{shoppingArticleCount ?? '—'}</dt><dd>{shoppingArticleLabel}</dd></div>
            <div><dt>{syncIssue ? 'À actualiser' : 'Synchronisée'}</dt><dd>cohérence Meal Prep</dd></div>
          </dl>
          {syncIssue && <p className="delivery-quick-review__note">{syncIssue}</p>}
          <button
            type="button"
            className="delivery-quick-review__toggle"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            {detailsOpen ? 'Masquer le détail' : 'Voir le détail'}
          </button>
        </section>
      )}

      <div hidden={compactReview && !detailsOpen && !editing} style={capsCardBodyStyle}>
        {(mealPrepValue && (syncRequired || syncIssue)) && (
          <section style={{
            marginBottom: 16,
            padding: 14,
            borderRadius: 10,
            border: `1px solid ${syncIssue ? 'rgba(229,200,120,.28)' : 'rgba(138,191,154,.28)'}`,
            background: syncIssue ? 'rgba(229,200,120,.055)' : 'rgba(138,191,154,.055)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 210 }}>
                <div style={{ ...capsSubTitleStyle, color: syncIssue ? '#e5c878' : '#a8e0c0' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Icon type={syncIssue ? "warning" : "check"} size={12} />
                    {syncIssue ? 'Dernière vérification' : 'Fiche frigo synchronisée'}
                  </span>
                </div>
                <p style={{ margin: '5px 0 0', color: '#99998d', fontSize: '.76rem', lineHeight: 1.5 }}>
                  {syncIssue
                    ? syncIssue
                    : 'Le Meal Prep, les ingrédients des recettes et les repères classiques correspondent à cette version.'}
                </p>
              </div>
              {syncIssue && !syncPreview && typeof onSaveFridgeSync === 'function' && (
                <button
                  type="button"
                  onClick={prepareSyncPreview}
                  disabled={saving || syncSaving || typeof onSaveFridgeSync !== 'function'}
                  style={{ ...syncButtonStyle, borderColor: 'rgba(138,191,154,.32)', color: '#a8e0c0' }}
                >Actualiser la fiche frigo</button>
              )}
            </div>

            {syncPreview && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(212,201,168,.12)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
                  <div style={{ ...capsPillStyle, borderRadius: 8 }}>
                    {syncPreview.sessionCount} session{syncPreview.sessionCount > 1 ? 's' : ''} Meal Prep
                  </div>
                  <div style={{ ...capsPillStyle, borderRadius: 8 }}>
                    {syncPreview.taskCount} préparation{syncPreview.taskCount > 1 ? 's' : ''}
                  </div>
                  <div style={{ ...capsPillStyle, borderRadius: 8 }}>
                    {syncPreview.ingredients.length} ingrédient{syncPreview.ingredients.length > 1 ? 's' : ''} des menus
                  </div>
                  <div style={{ ...capsPillStyle, borderRadius: 8 }}>
                    {syncPreview.classicItemCount} repère{syncPreview.classicItemCount > 1 ? 's' : ''} frigo
                  </div>
                </div>
                <p style={{ margin: '10px 0 0', color: '#77776e', fontSize: '.72rem', lineHeight: 1.45 }}>
                  Les ingrédients sont repris exactement des recettes validées. Les repères classiques restent inchangés.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                  <button type="button" onClick={() => setSyncPreview(null)} disabled={syncSaving} style={syncButtonStyle}>
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={confirmSync}
                    disabled={syncSaving}
                    style={{ ...syncButtonStyle, borderColor: 'rgba(138,191,154,.38)', color: '#a8e0c0', fontWeight: 700 }}
                  >{syncSaving ? 'Actualisation…' : 'Valider cette fiche frigo'}</button>
                </div>
              </div>
            )}
            {syncError && <p role="alert" style={{ margin: '10px 0 0', color: '#f5c6c6', fontSize: '.74rem', display: 'flex', alignItems: 'center', gap: 5 }}><Icon type="warning" size={12} />{syncError}</p>}
          </section>
        )}
        {empty ? (
          <p style={capsEmptyStyle}>Pas encore de fiche frigo</p>
        ) : editing ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 600, color: '#8abf9a', letterSpacing: '.08em', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                À privilégier
              </label>
              {renderPills(toFavor, setToFavor, 'favor')}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <input
                  type="text"
                  value={newFavor}
                  onChange={(e) => setNewFavor(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(toFavor, setToFavor, newFavor, setNewFavor); } }}
                  placeholder="Ajouter un aliment…"
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    background: 'rgba(0,0,0,0.18)',
                    border: '1px solid rgba(106, 191, 138, 0.20)',
                    borderRadius: 6,
                    color: '#f0f0e8',
                    fontFamily: 'inherit',
                    fontSize: '.85rem',
                  }}
                />
                <button
                  type="button"
                  onClick={() => addItem(toFavor, setToFavor, newFavor, setNewFavor)}
                  style={{
                    background: 'rgba(106, 191, 138, 0.12)',
                    border: '1px solid rgba(106, 191, 138, 0.28)',
                    color: '#a8e0c0',
                    padding: '8px 14px',
                    borderRadius: 6,
                    fontSize: '.82rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >＋ Ajouter</button>
              </div>
            </div>

            <div>
              <label style={{ fontSize: '.72rem', fontWeight: 600, color: '#d4806c', letterSpacing: '.08em', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                À limiter / éviter
              </label>
              {renderPills(toLimit, setToLimit, 'limit')}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <input
                  type="text"
                  value={newLimit}
                  onChange={(e) => setNewLimit(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(toLimit, setToLimit, newLimit, setNewLimit); } }}
                  placeholder="Ajouter un aliment à limiter…"
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    background: 'rgba(0,0,0,0.18)',
                    border: '1px solid rgba(212, 92, 76, 0.20)',
                    borderRadius: 6,
                    color: '#f0f0e8',
                    fontFamily: 'inherit',
                    fontSize: '.85rem',
                  }}
                />
                <button
                  type="button"
                  onClick={() => addItem(toLimit, setToLimit, newLimit, setNewLimit)}
                  style={{
                    background: 'rgba(212, 92, 76, 0.12)',
                    border: '1px solid rgba(212, 92, 76, 0.28)',
                    color: '#d4806c',
                    padding: '8px 14px',
                    borderRadius: 6,
                    fontSize: '.82rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >＋ Ajouter</button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 4 }}>
              {error && <span style={{ fontSize: '.75rem', color: '#f5c6c6', marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}><Icon type="warning" size={12} />{error}</span>}
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#8a8a7a',
                  padding: '8px 14px',
                  borderRadius: 6,
                  fontSize: '.82rem',
                  cursor: saving ? 'wait' : 'pointer',
                }}
              >Annuler</button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving || !dirty}
                style={{
                  background: dirty ? 'rgba(106, 191, 138, 0.15)' : 'rgba(106, 191, 138, 0.06)',
                  border: `1px solid ${dirty ? 'rgba(106, 191, 138, 0.35)' : 'rgba(106, 191, 138, 0.18)'}`,
                  color: dirty ? '#a8e0c0' : '#666',
                  padding: '8px 16px',
                  borderRadius: 6,
                  fontSize: '.82rem',
                  fontWeight: 600,
                  cursor: (saving || !dirty) ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
            </div>
          </div>
        ) : (
          <>
            {fridge?.essentials?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={capsSubTitleStyle}>{fridge.essentials_title || 'À privilégier'}</div>
                <div style={capsPillsStyle}>
                  {fridge.essentials.map((e) => (
                    <span key={e.id} style={capsPillStyle}>{e.label}</span>
                  ))}
                </div>
              </div>
            )}
            {fridge?.favorite?.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {fridge.favorite.map((cat) => (
                  <div key={cat.id} style={{ marginBottom: 8 }}>
                    <div style={capsSubTitleStyle}>{cat.title}</div>
                    <div style={capsPillsStyle}>
                      {cat.items?.map((it) => (
                        <span key={it.id} style={capsPillStyle}>{it.label}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {fridge?.limit?.length > 0 && (
              <div>
                {fridge.limit.map((cat) => (
                  <div key={cat.id} style={{ marginBottom: 8 }}>
                    <div style={capsSubTitleStyle}>{cat.title}</div>
                    <div style={capsPillsStyle}>
                      {cat.items?.map((it) => (
                        <span key={it.id} style={{ ...capsPillStyle, ...capsPillLimitStyle }}>{it.label}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <FridgeMealPrepDetails prep={weekMeals?.prep} />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Aperçu cliente de la semaine type (lecture seule) ─────────────────
//
// Les cartes repas telles que la cliente les voit. L'édition se fait dans
// WeekMenusEditor (4 semaines, un réservoir par repas).

function WeekMealsPreviewCard({ weekMeals }) {
  const meals = weekMeals?.days?.[0]?.meals || [];
  return (
    <div style={capsCardStyle}>
      <header style={capsCardHeaderStyle}>
        <Icon type="calendar" size={18} style={{ color: '#d4c9a8' }} />
        <div style={{ flex: 1 }}>
          <h4 style={capsCardTitleStyle}>Aperçu cliente — semaine type</h4>
          {weekMeals?.days?.length
            ? <p style={capsCardSubtitleStyle}>{weekMeals.days.length} jours · {meals.length} repas/jour</p>
            : null}
        </div>
      </header>
      <div style={capsCardBodyStyle}>
        {meals.length === 0 ? (
          <p style={capsEmptyStyle}>Aucun repas généré</p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {meals.map((m) => (
              <MealCardExpandable key={m.id} meal={m} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── V97.13.34 — Vue "page web cliente" pleine largeur ────────────────────
//
// Wrapper qui ajoute un header chaleureux style app cliente (greeting + meta)
// au-dessus des sections existantes (SectionsOverview). Donne à Anissa la
// sensation de voir l'expérience cliente complète en une vraie page web,
// pas un aperçu fragmenté.

function ClientFullPagePreview({ client, plan, children, onImprove, improving = false, showHero = true }) {
  const prenom = (client?.prenom || client?.form?.prenom || 'Cliente').trim();
  const pack = plan?.client_meta?.pack_label
    || plan?.client_meta?.pack
    || 'Accompagnement nutritionnel';

  // Pioche un premier signal narratif si dispo
  const intro = plan?.sections?.intro_data;
  const greetingLine = intro?.greeting?.trim()
    || `Bonjour ${prenom}, ton parcours commence aujourd'hui.`;

  // Compte rapide des éléments majeurs pour le sous-header
  const mealsCount = plan?.sections?.week_meals?.days?.[0]?.meals?.length || 0;
  const suppGroups = plan?.sections?.protocols_data?.groups?.length || 0;
  const fridgeItems = plan?.sections?.fridge_data?.essentials?.length || 0;

  return (
    <div className="jrn-clientpage">
      {/* Header chaleureux style app cliente */}
      {showHero && <div className="jrn-clientpage__hero">
        <div className="jrn-clientpage__hero-brand">
          <span className="jrn-clientpage__hero-avatar" aria-hidden>A</span>
          <span className="jrn-clientpage__hero-brandname">Anissa Deroubaix</span>
        </div>
        <h2 className="jrn-clientpage__hero-greeting">{greetingLine}</h2>
        <p className="jrn-clientpage__hero-sub">{pack}</p>
        <div className="jrn-clientpage__hero-stats">
          <span className="jrn-clientpage__hero-stat">
            <strong>{mealsCount || '—'}</strong>
            <span>repas / jour</span>
          </span>
          <span className="jrn-clientpage__hero-stat">
            <strong>{suppGroups || '—'}</strong>
            <span>moments de prise</span>
          </span>
          <span className="jrn-clientpage__hero-stat">
            <strong>{fridgeItems || '—'}</strong>
            <span>essentiels frigo</span>
          </span>
        </div>
        <div className="jrn-clientpage__hero-tag">
          Aperçu côté {prenom} — exactement ce qu'elle verra dans son app.
        </div>
      </div>}

      {/* Sections cliente — SectionsOverview existant injecté tel quel.
          Propage onImprove pour les boutons "Améliorer" par section. */}
      <div className="jrn-clientpage__sections">
        {typeof children === 'function'
          ? children({ onImprove, improving })
          : children}
      </div>
    </div>
  );
}

// Refonte du suivi (lot 5) : la partie « aperçu » de cette modale, en
// lecture seule, sur le livrable exact (payload) d'une version du suivi.
// Mêmes cartes que l'aperçu sections, sans aucune action d'édition.
export function ApercuApplication({ client, payload, consultation = null }) {
  const s = payload?.sections || {};
  const overrides = consultation?.editorial_overrides || {};
  return (
    <ClientFullPagePreview client={client} plan={payload}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <IntroCardEditable intro={s.intro_data} />
        <StrategyCardEditable strategy={s.strategy_data} />
        <WeekMealsPreviewCard weekMeals={s.week_meals} />
        <FridgeCardEditable
          fridge={s.fridge_data}
          weekMeals={s.week_meals}
          mealPrepValue={overrides.meal_prep}
          fridgeSyncValue={overrides.fridge_sync}
        />
        <SupplementsCardEditable protocols={s.protocols_data} supplementOverride={overrides.supplements} />
      </div>
    </ClientFullPagePreview>
  );
}

// ─── Mockup iPhone (legacy, conservé pour réutilisation future) ─────────────

function ClientPhoneMockup({ client, plan }) {
  const prenom = (client?.prenom || client?.form?.prenom || 'Cliente').trim();

  // Pioche le premier repas du plan si dispo, sinon fallback neutre.
  const firstDay = plan?.sections?.week_meals?.days?.[0];
  const firstMeal = firstDay?.meals?.[0];
  const mealLabel = firstMeal?.slot_label || 'Petit-déjeuner';
  const mealTitle = firstMeal?.title || 'Œufs · avocat · pain complet';

  // Premier groupe de compléments si dispo (pour donner du contenu réel)
  // Chemin correct : sections.protocols_data.groups (cf buildProtocolsData
  // dans clientAppMapper.js). Pas .supplements.* — corrigé V97.13.33.
  const supplementsGroups = plan?.sections?.protocols_data?.groups || [];
  const firstSuppGroup = supplementsGroups[0];
  const firstSuppItem = firstSuppGroup?.items?.[0];

  return (
    <div className="jrn-phone-mockup">
      <div className="jrn-phone-mockup__screen">
        {/* iOS-like status bar */}
        <div className="jrn-phone-mockup__statusbar">
          <span>9:24</span>
          <span className="jrn-phone-mockup__statusbar-right">
            <span className="jrn-phone-mockup__bar jrn-phone-mockup__bar--s" />
            <span className="jrn-phone-mockup__bar jrn-phone-mockup__bar--m" />
            <span className="jrn-phone-mockup__bar jrn-phone-mockup__bar--l" />
            <span className="jrn-phone-mockup__battery" />
          </span>
        </div>

        {/* Header — avatar Anissa + greeting cliente */}
        <div className="jrn-phone-mockup__header">
          <div className="jrn-phone-mockup__avatar">A</div>
          <div className="jrn-phone-mockup__header-text">
            <div className="jrn-phone-mockup__hi">Bonjour {prenom}</div>
            <div className="jrn-phone-mockup__sub">Jour 1 · semaine 1</div>
          </div>
        </div>

        {/* Mini courbe ressentis 7j (indicative) */}
        <div className="jrn-phone-mockup__chart">
          <div className="jrn-phone-mockup__chart-head">
            <span className="jrn-phone-mockup__chart-label">Énergie · 7 jours</span>
            {/* P3.1 — aucun chiffre clinique inventé, même en décoration :
                le mockup est un exemple, pas un vrai résultat cliente. */}
            <span className="jrn-phone-mockup__chart-trend">exemple</span>
          </div>
          <svg className="jrn-phone-mockup__chart-svg" viewBox="0 0 200 44" preserveAspectRatio="none">
            <defs>
              <linearGradient id="jrnAppPreviewArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1A2E1F" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#1A2E1F" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M5,36 Q35,32 60,26 T120,16 T195,7 L195,44 L5,44 Z" fill="url(#jrnAppPreviewArea)" />
            <path d="M5,36 Q35,32 60,26 T120,16 T195,7" stroke="#1A2E1F" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="5" cy="36" r="2.2" fill="#1A2E1F" />
            <circle cx="42" cy="30" r="2.2" fill="#1A2E1F" />
            <circle cx="80" cy="22" r="2.2" fill="#1A2E1F" />
            <circle cx="118" cy="16" r="2.2" fill="#1A2E1F" />
            <circle cx="156" cy="11" r="2.2" fill="#1A2E1F" />
            <circle cx="195" cy="7" r="3" fill="#fff" stroke="#1A2E1F" strokeWidth="2" />
          </svg>
        </div>

        {/* Card repas — pioche le vrai premier repas si dispo */}
        <div className="jrn-phone-mockup__card">
          <span className="jrn-phone-mockup__card-icon"><Icon type="plate" size={14} /></span>
          <div className="jrn-phone-mockup__card-body">
            <div className="jrn-phone-mockup__card-label">{mealLabel}</div>
            <div className="jrn-phone-mockup__card-text">{mealTitle}</div>
          </div>
          <span className="jrn-phone-mockup__check"><Icon type="check" size={12} /></span>
        </div>

        {/* Card complément si dispo, sinon fallback message */}
        {firstSuppItem ? (
          <div className="jrn-phone-mockup__card">
            <span className="jrn-phone-mockup__card-icon"><Icon type="package" size={14} /></span>
            <div className="jrn-phone-mockup__card-body">
              <div className="jrn-phone-mockup__card-label">{firstSuppGroup?.title || firstSuppGroup?.timing || 'Compléments'}</div>
              <div className="jrn-phone-mockup__card-text">{firstSuppItem.name}</div>
            </div>
          </div>
        ) : (
          <div className="jrn-phone-mockup__msg">
            <div className="jrn-phone-mockup__msg-avatar">A</div>
            <div className="jrn-phone-mockup__msg-bubble">
              Comment se passe ta semaine ?
            </div>
          </div>
        )}

        {/* Tab bar */}
        <div className="jrn-phone-mockup__tabbar">
          <span className="jrn-phone-mockup__tab jrn-phone-mockup__tab--active" />
          <span className="jrn-phone-mockup__tab" />
          <span className="jrn-phone-mockup__tab" />
          <span className="jrn-phone-mockup__tab" />
        </div>
      </div>
    </div>
  );
}
